import React, { useContext, useEffect, useMemo, useState } from "react";
import {
	Alert,
	Box,
	Button,
	Checkbox,
	CircularProgress,
	Divider,
	FormControl,
	FormControlLabel,
	FormGroup,
	InputLabel,
	InputAdornment,
	IconButton,
	MenuItem,
	Select,
	Slider,
	Stack,
	Switch,
	Tab,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Tabs,
	TextField,
	Typography,
	useTheme,Autocomplete
} from "@mui/material";
import Swal from "sweetalert2";
import { ThemeContext } from "../../context/ThemeContext";
import { fonts, themeColors } from "../../utilities/themeColors";
import { useTranslation } from "react-i18next";
import { THEME_TEXT } from "../../constants";
import DataGridSkeleton from "../../components/PageSkeletons/DataGridSkeleton";
import { insertOptions } from "../../services/defaultValuesService";
import {
	addBlockedDomains,
	getBlockedDomains,
	removeBlockedDomain,
} from "../../services/registrationBlockedDomainsService";
import { getRoles } from "../../services/createEditUSerFormService";
import { previewStepSyncReport, sendStepSyncReportNow } from "../../services/adminService";
import { useOptions } from "../../context/optionContext";
import { Save } from "@mui/icons-material";
import { useAuth } from "../../context/authContext";
import { DEFAULT_AXIS_MODEL_VIEWER, RSPL_LIST } from "../../config";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { ADMIN_MODULES, ADMIN_MODULE_KEYS, resolveEnabledAdminModules } from "../../utilities/adminModules";
import { getCustomerLocationOptionFlags, resolveServiceOptions, serializeServiceOptions } from "../../utilities/customerLocationSettings";
import { phoneCountries } from "../../data/phoneCountries";
import { buildCountryOptions, createCountryFilter, splitHighlightedText } from "../../utilities/countrySearchUtils";
import {
	DEFAULT_CASE_SAFETY_CONFIG,
	DEFAULT_CASE_TYPE_CONFIG,
	DEFAULT_SUPPORT_SETTINGS,
	SUPPORT_PHONE_PLACEHOLDER,
	isValidEmergencyPhone,
	resolveCaseSafetyConfig,
	resolveCaseTypeConfig,
	resolveSupportSettings,
} from "../../utilities/supportSettings";

const REGION_PHONE_DEFAULTS = [
	{ region: "Africa", iso2: "NG", phone: "+234 803 456 7890" },
	{ region: "Asia", iso2: "IN", phone: "+91 98765 43210" },
	{ region: "Canada", iso2: "CA", phone: "+1 416 555 2389" },
	{ region: "China", iso2: "CN", phone: "+86 138 1234 5678" },
	{ region: "Europe", iso2: "GB", phone: "+44 7700 900123" },
	{ region: "Latin America", iso2: "BR", phone: "+55 21 99876 5432" },
	{ region: "Middle East", iso2: "AE", phone: "+971 50 123 4567" },
	{ region: "America", iso2: "US", phone: "+1 305 678 9123" }
];

const REGION_DEFAULT_ISO2 = {
	Africa: "NG",
	Asia: "IN",
	Canada: "CA",
	China: "CN",
	Europe: "GB",
	"Latin America": "BR",
	"Middle East": "AE",
	America: "US",
};

const HEX_REGEX = /^#([0-9A-F]{6}|[0-9A-F]{3})$/i;
const isValidHex = (value = "") => HEX_REGEX.test(value.trim());
const MODEL_AXIS_CHOICES = ["X", "Y", "Z"];
const MODEL_CAMERA_CORNER_CHOICES = [
	"isometric",
	"front",
	"back",
	"left",
	"right",
	"top",
	"bottom",
];

const ADMIN_MODULE_GROUP_ORDER = [
	"Analytics",
	"User & Access",
	"Content & Communication",
	"Commerce",
	"Orders",
	"Machine & Product Admin",
	"System",
];

const normalizeModelAxis = (value, fallback = "Y") => {
	const raw = String(value || "")
		.trim()
		.toUpperCase()
		.replace(/[^A-Z]/g, "");
	if (raw === "X" || raw === "X_UP") return "X";
	if (raw === "Y" || raw === "Y_UP") return "Y";
	if (raw === "Z" || raw === "Z_UP") return "Z";
	return fallback;
};

const normalizeModelCameraCorner = (value, fallback = "isometric") => {
	const raw = String(value || "")
		.trim()
		.toLowerCase()
		.replace(/[_\s]+/g, "-")
		.replace(/[^a-z-]/g, "");
	return MODEL_CAMERA_CORNER_CHOICES.includes(raw) ? raw : fallback;
};

const getModelOrientationFromOptions = (optionKeys = {}) => {
	const fallbackUp = normalizeModelAxis(DEFAULT_AXIS_MODEL_VIEWER || "Y_UP", "Y");
	const up = normalizeModelAxis(
		optionKeys?.modelViewerOrientationUp || optionKeys?.modelViewerUpAxis,
		fallbackUp
	);
	const left = normalizeModelAxis(optionKeys?.modelViewerOrientationLeft, up === "X" ? "Z" : "X");
	const right = normalizeModelAxis(optionKeys?.modelViewerOrientationRight, "X");
	const isValid =
		MODEL_AXIS_CHOICES.includes(up) &&
		MODEL_AXIS_CHOICES.includes(left) &&
		MODEL_AXIS_CHOICES.includes(right) &&
		new Set([up, left, right]).size === 3;
	if (isValid) return { up, left, right };

	const fallbackLeft = up === "X" ? "Z" : "X";
	const fallbackRight =
		MODEL_AXIS_CHOICES.find((axis) => axis !== up && axis !== fallbackLeft) || "Y";
	return { up, left: fallbackLeft, right: fallbackRight };
};

const normalizeRsplList = (value) => {
	if (!value) return null;
	const parsed = Array.isArray(value)
		? value
		: (() => {
			try {
				return JSON.parse(value);
			} catch {
				return null;
			}
		})();
	if (!Array.isArray(parsed)) return null;
	const sanitized = parsed.filter(
		(item) =>
			item &&
			typeof item.value === "string" &&
			Array.isArray(item.key) &&
			item.key.every((entry) => typeof entry === "string")
	);
	return sanitized.length > 0 ? sanitized : null;
};

const rsplListToRows = (list) => {
	if (!Array.isArray(list)) return [];
	return list.flatMap((item) =>
		(item.key || []).map((key) => ({
			key,
			value: item.value || "",
		}))
	);
};

const rowsToRsplList = (rows) => {
	const grouped = new Map();
	rows.forEach((row) => {
		const key = (row.key || "").trim();
		const value = (row.value || "").trim();
		if (!key || !value) return;
		const bucket = grouped.get(value) || [];
		if (!bucket.includes(key)) bucket.push(key);
		grouped.set(value, bucket);
	});
	return Array.from(grouped.entries()).map(([value, key]) => ({ key, value }));
};

const parseEmergencyPhones = (raw) => {
	if (!raw) return null;
	try {
		const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
		if (!Array.isArray(parsed)) return null;
		return parsed
			.filter((row) => row && row.region)
			.map((row) => ({
				region: row.region,
				iso2: String(row.iso2 || REGION_DEFAULT_ISO2[row.region] || "US").toUpperCase(),
				phone: row.phone || "",
			}));
	} catch {
		return null;
	}
};

const sanitizeDialCode = (value = "") => String(value || "").replace(/[^\d]/g, "");

const replacePhoneDialCode = (phone = "", nextCountryCode = "") => {
	const trimmedPhone = String(phone || "").trim();
	const nextDialCode = sanitizeDialCode(nextCountryCode);
	if (!nextDialCode) return trimmedPhone;
	if (!trimmedPhone) return `+${nextDialCode} `;
	if (!trimmedPhone.startsWith("+")) return trimmedPhone;

	const currentDigits = trimmedPhone.slice(1).replace(/[^\d]/g, "");
	if (!currentDigits) return `+${nextDialCode} `;

	const matchingCountry = phoneCountries.find((country) => {
		const dial = sanitizeDialCode(country.phone);
		return dial && currentDigits.startsWith(dial);
	});
	if (!matchingCountry) return `+${nextDialCode} `;

	const currentDial = sanitizeDialCode(matchingCountry.phone);
	const remainderDigits = currentDigits.slice(currentDial.length);
	return remainderDigits ? `+${nextDialCode} ${remainderDigits}` : `+${nextDialCode} `;
};

const DEFAULT_ORDER_STATUS_MAPPINGS = [
	{ backendStatus: "Pending", label: "Pending", showInTracker: true },
	{ backendStatus: "Open Order", label: "Open Order", showInTracker: true },
	{ backendStatus: "Shipped", label: "Shipped", showInTracker: true },
	{ backendStatus: "Delivered", label: "Delivered", showInTracker: true },
	{ backendStatus: "Invoiced", label: "Invoiced", showInTracker: true },
	{ backendStatus: "Canceled", label: "Canceled", showInTracker: true },
];

const parseOrderStatusMappings = (raw, fallbackLabels = []) => {
	const fallback =
		Array.isArray(fallbackLabels) && fallbackLabels.length
			? fallbackLabels.map((label) => ({
				backendStatus: String(label || "").trim(),
				label: String(label || "").trim(),
				showInTracker: true,
			}))
			: DEFAULT_ORDER_STATUS_MAPPINGS;
	if (!raw) return fallback;
	try {
		const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
		if (!Array.isArray(parsed)) return fallback;
		const sanitized = parsed
			.map((item) => ({
				backendStatus: String(item?.backendStatus || "").trim(),
				label: String(item?.label || "").trim(),
				showInTracker: item?.showInTracker !== false && item?.showInTracker !== "false",
			}))
			.filter((item) => item.backendStatus && item.label);
		return sanitized.length ? sanitized : fallback;
	} catch {
		return fallback;
	}
};

const parseJsonStringArray = (raw, fallback = []) => {
	if (!raw) return fallback;
	try {
		const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
		if (!Array.isArray(parsed)) return fallback;
		return parsed
			.map((value) => String(value || "").trim())
			.filter(Boolean);
	} catch {
		return fallback;
	}
};

const PRODUCT_DEFAULT_SORT_OPTIONS = [
	{ key: "name_asc", label: "Name Asc" },
	{ key: "name_desc", label: "Name Desc" },
	{ key: "number_asc", label: "Number Asc" },
	{ key: "number_desc", label: "Number Desc" },
	{ key: "price_asc", label: "Price Asc" },
	{ key: "price_desc", label: "Price Desc" },
];

const serializeOrderStatusMappings = (mappings = []) => {
	const sanitized = (Array.isArray(mappings) ? mappings : [])
		.map((item) => ({
			backendStatus: String(item?.backendStatus || "").trim(),
			label: String(item?.label || "").trim(),
			showInTracker: item?.showInTracker !== false,
		}))
		.filter((item) => item.backendStatus && item.label);
	return JSON.stringify(sanitized.length ? sanitized : DEFAULT_ORDER_STATUS_MAPPINGS);
};

const Settings = () => {
	const { t } = useTranslation();
	const {
		toggleMode,
		changeAccentColor,
		mode,
		accentColor,
		headerFontKey,
		bodyFontKey,
		headerFontWeight,
		bodyFontWeight,
		setHeaderFontKey,
		setBodyFontKey,
		setHeaderFontWeight,
		setBodyFontWeight,
	} = useContext(ThemeContext);
	const { user } = useAuth();
	const { optionKeys, fetchOptions, optionsLoaded, optionsError } = useOptions();
	const { theme } = useTheme()
	const [selectedColor, setSelectedColor] = useState(accentColor || "blue");
	const [customColor, setCustomColor] = useState("");
	const [customColorError, setCustomColorError] = useState("");
	const [saving, setSaving] = useState(false);
	const [siteGroupingEnabled, setSiteGroupingEnabled] = useState(true);
	const [siteNumberVisible, setSiteNumberVisible] = useState(true);
	const isSuperAdmin = user?.user_id === 1;
	const [rsplRows, setRsplRows] = useState(rsplListToRows(RSPL_LIST));
	const [rsplError, setRsplError] = useState("");
	const [rsplSaving, setRsplSaving] = useState(false);
	const [siteSaving, setSiteSaving] = useState(false);
	const [tab, setTab] = useState(0);
	const initialModelOrientation = useMemo(() => getModelOrientationFromOptions({}), []);
	const [modelOrientationUp, setModelOrientationUp] = useState(initialModelOrientation.up);
	const [modelOrientationLeft, setModelOrientationLeft] = useState(initialModelOrientation.left);
	const [modelOrientationRight, setModelOrientationRight] = useState(initialModelOrientation.right);
	const [modelDefaultCameraCorner, setModelDefaultCameraCorner] = useState("isometric");
	const [modelAxisSaving, setModelAxisSaving] = useState(false);
	const [modelLightIntensity, setModelLightIntensity] = useState(1);
	const [modelHighlightColor, setModelHighlightColor] = useState("");
	const [modelHighlightOpacity, setModelHighlightOpacity] = useState(1);
	const [modelMenuTabSubassemblies, setModelMenuTabSubassemblies] = useState("Sub-Assemblies");
	const [modelMenuTabRspl, setModelMenuTabRspl] = useState("RSPL Parts");
	const [modelMenuTabPartsTree, setModelMenuTabPartsTree] = useState("Parts Tree");
	const [modelMenuShowSubtitles, setModelMenuShowSubtitles] = useState(true);
	const [enableTours, setEnableTours] = useState(true);
	const [enableChatbot, setEnableChatbot] = useState(true);
	const [enableLanguageTranslation, setEnableLanguageTranslation] = useState(true);
	const [defaultLanguage, setDefaultLanguage] = useState("en");
	const [showOrderHistoryPricesAll, setShowOrderHistoryPricesAll] = useState(true);
	const [showOrderHistoryPricesPast, setShowOrderHistoryPricesPast] = useState(true);
	const [enableOrderSchedules, setEnableOrderSchedules] = useState(true);
	const [enableCheckoutAddressEditing, setEnableCheckoutAddressEditing] = useState(true);
	const [enableMachineSellNotifyForm, setEnableMachineSellNotifyForm] = useState(false);
	const [productsDefaultFilterTypes, setProductsDefaultFilterTypes] = useState(["__ALL__"]);
	const [productsDefaultSortKey, setProductsDefaultSortKey] = useState("price_desc");
	const [orderStatusLabels, setOrderStatusLabels] = useState(
		"Pending, Open Order, Shipped, Delivered, Invoiced, Canceled"
	);
	const [orderStatusMappings, setOrderStatusMappings] = useState(DEFAULT_ORDER_STATUS_MAPPINGS);
	const [featureSaving, setFeatureSaving] = useState(false);
	const [blockedDomains, setBlockedDomains] = useState([]);
	const [blockedDomainsInput, setBlockedDomainsInput] = useState("");
	const [blockedDomainsLoading, setBlockedDomainsLoading] = useState(false);
	const [blockedDomainsError, setBlockedDomainsError] = useState("");
	const [blockedDomainsSaving, setBlockedDomainsSaving] = useState(false);
	const [adminModulesSaving, setAdminModulesSaving] = useState(false);
	const [adminEnabledModules, setAdminEnabledModules] = useState(ADMIN_MODULE_KEYS);
	const groupedAdminModules = useMemo(() => {
		const modulesByGroup = ADMIN_MODULES.reduce((groups, module) => {
			const group = module.group || "System";
			return {
				...groups,
				[group]: [...(groups[group] || []), module],
			};
		}, {});
		return ADMIN_MODULE_GROUP_ORDER
			.filter((group) => modulesByGroup[group]?.length)
			.map((group) => ({ group, modules: modulesByGroup[group] }));
	}, []);
	const [footerShowVersion, setFooterShowVersion] = useState(true);
	const [footerShowSupport, setFooterShowSupport] = useState(true);
	const [footerShowTerms, setFooterShowTerms] = useState(true);
	const [footerShowEula, setFooterShowEula] = useState(true);
	const [footerShowPrivacy, setFooterShowPrivacy] = useState(true);
	const [footerShowCookiePolicy, setFooterShowCookiePolicy] = useState(true);
	const [footerShowNeedHelp, setFooterShowNeedHelp] = useState(true);
	const [footerShowManual, setFooterShowManual] = useState(true);
	const [footerSaving, setFooterSaving] = useState(false);
	const [cookieConsentRePromptDays, setCookieConsentRePromptDays] = useState("30");
	const [cookieSettingsSaving, setCookieSettingsSaving] = useState(false);
	const [missingSiteConfigRoles, setMissingSiteConfigRoles] = useState([]);
	const [missingSiteConfigUseCcr, setMissingSiteConfigUseCcr] = useState(false);
	const [missingSiteConfigSaving, setMissingSiteConfigSaving] = useState(false);
	const [roleOptions, setRoleOptions] = useState([]);
	const [stepSyncReportEnabled, setStepSyncReportEnabled] = useState(true);
	const [stepSyncSuccessRoleIds, setStepSyncSuccessRoleIds] = useState([]);
	const [stepSyncReportSendTime, setStepSyncReportSendTime] = useState("05:00");
	const [stepSyncReportSaving, setStepSyncReportSaving] = useState(false);
	const [stepSyncReportTesting, setStepSyncReportTesting] = useState("");
	const [customerLocationServicesEnabled, setCustomerLocationServicesEnabled] = useState(true);
	const [customerLocationServiceRows, setCustomerLocationServiceRows] = useState([]);
	const [customerLocationServiceError, setCustomerLocationServiceError] = useState("");
	const [customerLocationShowLegalEntity, setCustomerLocationShowLegalEntity] = useState(true);
	const [customerLocationShowSite, setCustomerLocationShowSite] = useState(true);
	const [customerLocationShowWarehouse, setCustomerLocationShowWarehouse] = useState(true);
	const [customerLocationShowPaymentTerms, setCustomerLocationShowPaymentTerms] = useState(true);
	const [customerLocationShowOnHold, setCustomerLocationShowOnHold] = useState(true);
	const [customerLocationSaving, setCustomerLocationSaving] = useState(false);
	const [customerLocationEmergencyPhones, setCustomerLocationEmergencyPhones] = useState(REGION_PHONE_DEFAULTS);
	const [supportInfoSaving, setSupportInfoSaving] = useState(false);
	const [supportContactCcrShowEmergencyMessage, setSupportContactCcrShowEmergencyMessage] = useState(DEFAULT_SUPPORT_SETTINGS.contactCcrShowEmergencyMessage);
	const [supportContactCcrEmergencyNumberSource, setSupportContactCcrEmergencyNumberSource] = useState(DEFAULT_SUPPORT_SETTINGS.contactCcrEmergencyNumberSource);
	const [supportContactCcrEmergencyNumberManual, setSupportContactCcrEmergencyNumberManual] = useState(DEFAULT_SUPPORT_SETTINGS.contactCcrEmergencyNumberManual);
	const [supportCreateCaseShowEmergencyMessage, setSupportCreateCaseShowEmergencyMessage] = useState(DEFAULT_SUPPORT_SETTINGS.createCaseShowEmergencyMessage);
	const [supportCreateCaseEmergencyNumberSource, setSupportCreateCaseEmergencyNumberSource] = useState(DEFAULT_SUPPORT_SETTINGS.createCaseEmergencyNumberSource);
	const [supportCreateCaseEmergencyNumberManual, setSupportCreateCaseEmergencyNumberManual] = useState(DEFAULT_SUPPORT_SETTINGS.createCaseEmergencyNumberManual);
	const [supportEnableErpCaseCreation, setSupportEnableErpCaseCreation] = useState(DEFAULT_SUPPORT_SETTINGS.enableErpCaseCreation);
	const [supportCreateCaseFieldVisibility, setSupportCreateCaseFieldVisibility] = useState(DEFAULT_SUPPORT_SETTINGS.createCaseFieldVisibility);
	const [supportCaseTypeConfig, setSupportCaseTypeConfig] = useState(DEFAULT_CASE_TYPE_CONFIG);
	const [supportCaseSafetyConfig, setSupportCaseSafetyConfig] = useState(DEFAULT_CASE_SAFETY_CONFIG);

	const presetColors = useMemo(
		() => Object.entries(themeColors).map(([key, palette]) => ({ key, palette })),
		[]
	);
	const fontWeightOptions = useMemo(
		() => [
			{ value: "", label: t("UI_TEXT.FONT_WEIGHT_DEFAULT", { defaultValue: "Default" }) },
			{ value: "300", label: t("UI_TEXT.FONT_WEIGHT_LIGHT", { defaultValue: "Light (300)" }) },
			{ value: "400", label: t("UI_TEXT.FONT_WEIGHT_REGULAR", { defaultValue: "Regular (400)" }) },
			{ value: "500", label: t("UI_TEXT.FONT_WEIGHT_MEDIUM", { defaultValue: "Medium (500)" }) },
			{ value: "600", label: t("UI_TEXT.FONT_WEIGHT_SEMIBOLD", { defaultValue: "Semi Bold (600)" }) },
			{ value: "700", label: t("UI_TEXT.FONT_WEIGHT_BOLD", { defaultValue: "Bold (700)" }) },
		],
		[t]
	);
	const languageOptions = useMemo(
		() => [
			{ value: "en", label: t("LABELS.LANG_ENGLISH", { defaultValue: "English" }) },
			{ value: "fr", label: t("UI_TEXT.LANG_FRENCH", { defaultValue: "French" }) },
			{ value: "es", label: t("UI_TEXT.LANG_SPANISH", { defaultValue: "Spanish" }) },
			{ value: "de", label: t("UI_TEXT.LANG_GERMAN", { defaultValue: "German" }) },
		],
		[t]
	);
	const productDefaultFilterOptions = useMemo(() => {
		const labels = rsplRows
			.map((row) => String(row?.value || "").trim())
			.filter(Boolean);
		labels.push(t("UI_TEXT.OTHER", { defaultValue: "Other" }));
		return Array.from(new Set(labels));
	}, [rsplRows, t]);
	const phoneCountryOptions = useMemo(() => buildCountryOptions(phoneCountries), []);
	const phoneCountryFilter = useMemo(() => createCountryFilter(), []);

	useEffect(() => {
		if (!optionKeys) return;
		const stored = optionKeys.accentColor || optionKeys.theme;
		if (stored) {
			setSelectedColor(stored);
			setCustomColor(isValidHex(stored) ? stored : "");
			setCustomColorError("");
		}
		setSiteGroupingEnabled(optionKeys.siteSelectionGroupingEnabled !== "false");
		setSiteNumberVisible(optionKeys.siteSelectionShowSiteNumber !== "false");
		const rsplSource = normalizeRsplList(optionKeys.rsplList) || RSPL_LIST;
		setRsplRows(rsplListToRows(rsplSource));
		setRsplError("");
		const nextOrientation = getModelOrientationFromOptions(optionKeys);
		setModelOrientationUp(nextOrientation.up);
		setModelOrientationLeft(nextOrientation.left);
		setModelOrientationRight(nextOrientation.right);
		setModelDefaultCameraCorner(
			normalizeModelCameraCorner(optionKeys?.modelViewerDefaultCameraCorner, "isometric")
		);
		const nextLight = Number.parseFloat(optionKeys.modelViewerLightIntensity);
		setModelLightIntensity(Number.isFinite(nextLight) ? nextLight : 1);
		const highlightColor = optionKeys.modelViewerHighlightColor;
		const fallbackHighlight = themeColors[accentColor]
			? accentColor
			: (isValidHex(accentColor)
				? accentColor
				: theme?.palette?.primary?.light || "#ffcc00");
		if (themeColors[highlightColor]) {
			setModelHighlightColor(highlightColor);
		} else {
			setModelHighlightColor(isValidHex(highlightColor) ? highlightColor : fallbackHighlight);
		}
		const nextOpacity = Number.parseFloat(optionKeys.modelViewerHighlightOpacity);
		setModelHighlightOpacity(Number.isFinite(nextOpacity) ? Math.min(1, Math.max(0, nextOpacity)) : 1);
		setModelMenuTabSubassemblies(optionKeys.modelViewerTabTitleSubAssemblies || "Sub-Assemblies");
		setModelMenuTabRspl(optionKeys.modelViewerTabTitleRspl || "RSPL Parts");
		setModelMenuTabPartsTree(optionKeys.modelViewerTabTitlePartsTree || "Parts Tree");
		setModelMenuShowSubtitles(optionKeys.modelViewerShowTabSubtitles !== "false");
		setEnableTours(optionKeys.enableTours !== "false");
		setEnableChatbot(optionKeys.enableChatbot !== "false");
		setEnableLanguageTranslation(optionKeys.enableLanguageTranslation !== "false");
		const nextDefaultLanguage = optionKeys.defaultLanguage || "en";
		setDefaultLanguage(
			languageOptions.some((lang) => lang.value === nextDefaultLanguage) ? nextDefaultLanguage : "en"
		);
		const nextShowAllOrderHistoryPrices = optionKeys.showOrderHistoryPricesAll !== "false";
		setShowOrderHistoryPricesAll(nextShowAllOrderHistoryPrices);
		setShowOrderHistoryPricesPast(
			nextShowAllOrderHistoryPrices && optionKeys.showOrderHistoryPricesPast !== "false"
		);
		setEnableOrderSchedules(optionKeys.enableOrderSchedules !== "false");
		setEnableCheckoutAddressEditing(optionKeys.enableCheckoutAddressEditing !== "false");
		setEnableMachineSellNotifyForm(
			String(optionKeys.enableMachineSellNotifyForm ?? "false").trim().toLowerCase() === "true"
		);
		const nextSortKey = String(optionKeys.productsDefaultSortKey || "price_desc").trim().toLowerCase();
		const allowedSortKeys = new Set(PRODUCT_DEFAULT_SORT_OPTIONS.map((item) => item.key));
		setProductsDefaultSortKey(allowedSortKeys.has(nextSortKey) ? nextSortKey : "price_desc");
		const nextDefaultFilterTypes = parseJsonStringArray(
			optionKeys.productsDefaultFilterTypes,
			["__ALL__"]
		);
		setProductsDefaultFilterTypes(
			nextDefaultFilterTypes.length > 0 ? nextDefaultFilterTypes : ["__ALL__"]
		);
		const nextOrderStatusLabels =
			optionKeys.orderStatusLabels ||
			"Pending, Open Order, Shipped, Delivered, Invoiced, Canceled";
		setOrderStatusLabels(nextOrderStatusLabels);
		const fallbackOrderStatusLabels = String(nextOrderStatusLabels)
			.split(/[\n,]+/)
			.map((value) => value.trim())
			.filter(Boolean);
		setOrderStatusMappings(
			parseOrderStatusMappings(optionKeys.orderStatusMappings, fallbackOrderStatusLabels)
		);
		setAdminEnabledModules(resolveEnabledAdminModules(optionKeys));
		setFooterShowVersion(optionKeys.footerShowVersion !== "false");
		setFooterShowSupport(optionKeys.footerShowSupport !== "false");
		setFooterShowTerms(optionKeys.footerShowTerms !== "false");
		setFooterShowEula(optionKeys.footerShowEula !== "false");
		setFooterShowPrivacy(optionKeys.footerShowPrivacy !== "false");
		setFooterShowCookiePolicy(optionKeys.footerShowCookiePolicy !== "false");
		setFooterShowManual(optionKeys.footerShowManual !== "false");
		setFooterShowNeedHelp(optionKeys.footerShowNeedHelp !== "false");
		setStepSyncReportEnabled(optionKeys.stepSyncReportEnabled !== "false");
		setStepSyncSuccessRoleIds(
			String(optionKeys.stepSyncReportSuccessRoles || "")
				.split(",")
				.map((v) => v.trim())
				.filter(Boolean)
		);
		setStepSyncReportSendTime(
			/^\d{1,2}:\d{2}$/.test(String(optionKeys.stepSyncReportSendTime || "").trim())
				? String(optionKeys.stepSyncReportSendTime).trim().padStart(5, "0")
				: "05:00"
		);
		setMissingSiteConfigRoles(parseJsonStringArray(optionKeys.missingSiteConfigNotifyRoles, []));
		setMissingSiteConfigUseCcr(String(optionKeys.missingSiteConfigNotifyCcr) === "true");
		const nextCookieDays = Number.parseInt(optionKeys.cookieConsentRePromptDays, 10);
		setCookieConsentRePromptDays(
			Number.isFinite(nextCookieDays) && nextCookieDays > 0 ? String(nextCookieDays) : "30"
		);
		const locationFlags = getCustomerLocationOptionFlags(optionKeys);
		setCustomerLocationServicesEnabled(locationFlags.servicesEnabled);
		setCustomerLocationShowLegalEntity(locationFlags.showLegalEntity);
		setCustomerLocationShowSite(locationFlags.showSite);
		setCustomerLocationShowWarehouse(locationFlags.showWarehouse);
		setCustomerLocationShowPaymentTerms(locationFlags.showPaymentTerms);
		setCustomerLocationShowOnHold(locationFlags.showOnHold);
		const services = resolveServiceOptions(optionKeys);
		setCustomerLocationServiceRows(
			Array.isArray(services)
				? services.map((service) => ({
					key: service.key || "",
					label: service.label || "",
					description: service.description || "",
					href: service.href || "",
				}))
				: []
		);
		const emergencyPhones = parseEmergencyPhones(optionKeys.customerLocationEmergencyPhones);
		setCustomerLocationEmergencyPhones(emergencyPhones && emergencyPhones.length ? emergencyPhones : REGION_PHONE_DEFAULTS);
		const supportSettings = resolveSupportSettings(optionKeys);
		setSupportContactCcrShowEmergencyMessage(supportSettings.contactCcrShowEmergencyMessage);
		setSupportContactCcrEmergencyNumberSource(supportSettings.contactCcrEmergencyNumberSource);
		setSupportContactCcrEmergencyNumberManual(supportSettings.contactCcrEmergencyNumberManual);
		setSupportCreateCaseShowEmergencyMessage(supportSettings.createCaseShowEmergencyMessage);
		setSupportCreateCaseEmergencyNumberSource(supportSettings.createCaseEmergencyNumberSource);
		setSupportCreateCaseEmergencyNumberManual(supportSettings.createCaseEmergencyNumberManual);
		setSupportEnableErpCaseCreation(supportSettings.enableErpCaseCreation);
		setSupportCreateCaseFieldVisibility({
			...DEFAULT_SUPPORT_SETTINGS.createCaseFieldVisibility,
			...(supportSettings.createCaseFieldVisibility || {}),
		});
		setSupportCaseTypeConfig(resolveCaseTypeConfig(optionKeys));
		setSupportCaseSafetyConfig(resolveCaseSafetyConfig(optionKeys));
		setCustomerLocationServiceError("");
	}, [optionKeys, languageOptions, theme, accentColor, presetColors]);

	useEffect(() => {
		if (!accentColor) return;
		setSelectedColor(accentColor);
		setCustomColor(isValidHex(accentColor) ? accentColor : "");
		setCustomColorError("");
	}, [accentColor]);

	const handleSelectAccent = (colorKey) => {
		setSelectedColor(colorKey);
		setCustomColor("");
		setCustomColorError("");
		changeAccentColor(colorKey);
	};

	const handleCustomColorChange = (value) => {
		setCustomColor(value);
		if (!value) {
			setCustomColorError("");
			return;
		}
		if (isValidHex(value)) {
			setCustomColorError("");
			setSelectedColor(value);
			changeAccentColor(value);
		} else {
			setCustomColorError(
				t("ALERT_MESSAGES.INVALID_HEX_COLOR", {
					defaultValue: "Enter a valid hex color (ex: #0066FF)",
				})
			);
		}
	};

	const resolveColorToSave = () => {
		if (themeColors[selectedColor]) return selectedColor;
		if (isValidHex(selectedColor)) return selectedColor;
		if (themeColors[accentColor] || isValidHex(accentColor)) return accentColor;
		return "blue";
	};

	/**
	 * Send only the options the user actually changed, never the whole tab.
	 *
	 * Every Save button used to POST its entire bundle of keys, so a field that
	 * had never hydrated (a failed or still-running options fetch) was written
	 * back as its blank initial default and silently overwrote real production
	 * configuration. Comparing against `optionKeys` — the values the server last
	 * returned — means an untouched field is simply not in the request.
	 *
	 * Both sides are compared as strings because `options` is an EAV table whose
	 * values are always text, while the form holds booleans, numbers and JSON.
	 * A key absent from `optionKeys` compares against "" and therefore counts as
	 * changed unless the new value is blank too, so first-time saves still work.
	 *
	 * @param {Array<{option_key: string, option_value: *}>} payload Full option bundle for the tab.
	 * @returns {Promise<Object|null>} The `insertOptions` response, or `null` when nothing changed (the caller must stop, the info toast has already been shown).
	 */
	const saveChangedOptions = async (payload) => {
		const changed = (payload || []).filter(
			(item) =>
				String(item?.option_value ?? "") !== String(optionKeys?.[item?.option_key] ?? "")
		);
		if (!changed.length) {
			Swal.fire({
				icon: "info",
				title: t("ALERT_MESSAGES.INFO", { defaultValue: "Info" }),
				text: t("UI_TEXT.NO_CHANGES_TO_SAVE", { defaultValue: "No changes to save" }),
			});
			return null;
		}
		return insertOptions({ options: changed });
	};

	const handleSaveTheme = async () => {
		if (customColor && customColorError) {
			return;
		}
		const accentToSave = resolveColorToSave();
		const payload = [
			{ option_key: "accentColor", option_value: accentToSave },
			{ option_key: "headerFontKey", option_value: headerFontKey },
			{ option_key: "bodyFontKey", option_value: bodyFontKey },
			{ option_key: "headerFontWeight", option_value: headerFontWeight === null ? "" : String(headerFontWeight) },
			{ option_key: "bodyFontWeight", option_value: bodyFontWeight === null ? "" : String(bodyFontWeight) },
		];
		setSaving(true);
		try {
			const resp = await saveChangedOptions(payload);
			if (!resp) return;
			if (!resp?.status) {
				throw new Error(resp?.message || "Failed to save theme options");
			}
			Swal.fire({
				icon: "success",
				title: t("ALERT_MESSAGES.SUCCESS", { defaultValue: "Success" }),
				text:
					resp?.message ||
					t("UI_TEXT.THEME_SAVED", { defaultValue: "Theme updated successfully" }),
			});
			await fetchOptions();
		} catch (error) {
			Swal.fire({
				icon: "error",
				title: t("ALERT_MESSAGES.ERROR", { defaultValue: "Error" }),
				text: error?.message || t("UI_TEXT.ERROR_SAVING", { defaultValue: "Failed to save changes" }),
			});
		} finally {
			setSaving(false);
		}
	};

	const handleSaveFeatureSettings = async () => {
		setFeatureSaving(true);
		try {
			const payload = [
				{ option_key: "enableTours", option_value: String(enableTours) },
				{ option_key: "enableChatbot", option_value: String(enableChatbot) },
				{ option_key: "enableLanguageTranslation", option_value: String(enableLanguageTranslation) },
				{ option_key: "defaultLanguage", option_value: defaultLanguage },
				{ option_key: "showOrderHistoryPricesAll", option_value: String(showOrderHistoryPricesAll) },
				{ option_key: "showOrderHistoryPricesPast", option_value: String(showOrderHistoryPricesPast) },
				{ option_key: "enableOrderSchedules", option_value: String(enableOrderSchedules) },
				{ option_key: "enableCheckoutAddressEditing", option_value: String(enableCheckoutAddressEditing) },
				{ option_key: "enableMachineSellNotifyForm", option_value: String(enableMachineSellNotifyForm) },
				{ option_key: "productsDefaultSortKey", option_value: productsDefaultSortKey },
				{
					option_key: "productsDefaultFilterTypes",
					option_value: JSON.stringify(
						productsDefaultFilterTypes.includes("__ALL__")
							? ["__ALL__"]
							: productsDefaultFilterTypes
					),
				},
				{ option_key: "orderStatusLabels", option_value: orderStatusLabels },
				{ option_key: "orderStatusMappings", option_value: serializeOrderStatusMappings(orderStatusMappings) },
			];
			const resp = await saveChangedOptions(payload);
			if (!resp) return;
			if (!resp?.status) {
				throw new Error(resp?.message || "Failed to save feature settings");
			}
			Swal.fire({
				icon: "success",
				title: t("ALERT_MESSAGES.SUCCESS", { defaultValue: "Success" }),
				text: t("LABELS.SETTINGS_UPDATED_SUCCESSFULLY", { defaultValue: "Settings updated successfully" }),
			});
			await fetchOptions();
		} catch (error) {
			Swal.fire({
				icon: "error",
				title: t("ALERT_MESSAGES.ERROR", { defaultValue: "Error" }),
				text: error?.message || t("UI_TEXT.ERROR_SAVING", { defaultValue: "Failed to save changes" }),
			});
		} finally {
			setFeatureSaving(false);
		}
	};

	/**
	 * Load the blocked public-registration email domains.
	 *
	 * Kept out of the options bundle on purpose: the list is its own resource now,
	 * so a Settings tab that fails to load can no longer save an empty value over
	 * it. A failed load is recorded in `blockedDomainsError`, which the UI uses to
	 * disable Add and Remove — acting on a list that may not be the real one is
	 * how the blank-save incident happened in the first place.
	 *
	 * @returns {Promise<void>} Resolves once the request settles; never rejects.
	 */
	const loadBlockedDomains = async () => {
		setBlockedDomainsLoading(true);
		try {
			const resp = await getBlockedDomains();
			if (!resp?.status) {
				setBlockedDomains([]);
				setBlockedDomainsError(
					resp?.message ||
					t("UI_TEXT.BLOCKED_DOMAINS_LOAD_FAILED", {
						defaultValue: "Failed to load blocked registration domains",
					})
				);
				return;
			}
			setBlockedDomains(Array.isArray(resp.data) ? resp.data : []);
			setBlockedDomainsError("");
		} catch (error) {
			setBlockedDomains([]);
			setBlockedDomainsError(
				error?.message ||
				t("UI_TEXT.BLOCKED_DOMAINS_LOAD_FAILED", {
					defaultValue: "Failed to load blocked registration domains",
				})
			);
		} finally {
			setBlockedDomainsLoading(false);
		}
	};

	/**
	 * Block every domain typed into the input.
	 *
	 * The box accepts several domains at once separated by comma, semicolon,
	 * space or newline, because that is how the list was pasted around when it
	 * was a single free-text option — keeping that paste working avoids people
	 * adding thirty domains one at a time. Normalisation and validation stay on
	 * the server; this only splits and drops empties.
	 *
	 * @returns {Promise<void>} Resolves once the add round-trip finishes.
	 */
	const handleAddBlockedDomains = async () => {
		const domains = String(blockedDomainsInput || "")
			.split(/[\s,;]+/)
			.map((value) => value.trim())
			.filter(Boolean);
		if (!domains.length) {
			Swal.fire({
				icon: "warning",
				title: t("ALERT_MESSAGES.WARNING", { defaultValue: "Warning" }),
				text: t("UI_TEXT.BLOCKED_DOMAINS_REQUIRED", {
					defaultValue: "Enter at least one domain to block.",
				}),
			});
			return;
		}
		setBlockedDomainsSaving(true);
		try {
			const resp = await addBlockedDomains(domains);
			if (!resp?.status) {
				throw new Error(
					resp?.message ||
					t("UI_TEXT.BLOCKED_DOMAINS_ADD_FAILED", { defaultValue: "Failed to block domains" })
				);
			}
			// The response carries the refreshed list, so there is no second GET.
			setBlockedDomains(Array.isArray(resp?.data?.domains) ? resp.data.domains : []);
			setBlockedDomainsInput("");
			setBlockedDomainsError("");
			Swal.fire({
				icon: "success",
				title: t("ALERT_MESSAGES.SUCCESS", { defaultValue: "Success" }),
				text:
					resp?.message ||
					t("UI_TEXT.BLOCKED_DOMAINS_ADDED", { defaultValue: "Blocked domains updated" }),
			});
		} catch (error) {
			Swal.fire({
				icon: "error",
				title: t("ALERT_MESSAGES.ERROR", { defaultValue: "Error" }),
				text:
					error?.message ||
					t("UI_TEXT.BLOCKED_DOMAINS_ADD_FAILED", { defaultValue: "Failed to block domains" }),
			});
		} finally {
			setBlockedDomainsSaving(false);
		}
	};

	/**
	 * Unblock one domain, after confirmation.
	 *
	 * Confirmed because removing a row immediately re-opens public registration
	 * to that domain and there is no undo.
	 *
	 * @param {{id: number|string, domain: string}} row The row to remove.
	 * @returns {Promise<void>} Resolves once the delete round-trip finishes, or immediately if cancelled.
	 */
	const handleRemoveBlockedDomain = async (row) => {
		const confirmation = await Swal.fire({
			icon: "warning",
			title: t("ALERT_MESSAGES.ARE_YOU_SURE", { defaultValue: "Are you sure?" }),
			text: t("UI_TEXT.BLOCKED_DOMAINS_REMOVE_CONFIRM", {
				domain: row?.domain,
				defaultValue:
					"Unblock {{domain}}? Anyone with that email domain will be able to register.",
			}),
			showCancelButton: true,
			confirmButtonText: t("BUTTON.YES", { defaultValue: "Yes" }),
			cancelButtonText: t("BUTTON.CANCEL", { defaultValue: "Cancel" }),
		});
		if (!confirmation.isConfirmed) return;
		setBlockedDomainsSaving(true);
		try {
			const resp = await removeBlockedDomain(row?.id);
			if (!resp?.status) {
				throw new Error(
					resp?.message ||
					t("UI_TEXT.BLOCKED_DOMAINS_REMOVE_FAILED", { defaultValue: "Failed to unblock domain" })
				);
			}
			setBlockedDomains(Array.isArray(resp?.data?.domains) ? resp.data.domains : []);
			setBlockedDomainsError("");
			Swal.fire({
				icon: "success",
				title: t("ALERT_MESSAGES.SUCCESS", { defaultValue: "Success" }),
				text:
					resp?.message ||
					t("UI_TEXT.BLOCKED_DOMAINS_REMOVED", { defaultValue: "Blocked domains updated" }),
			});
		} catch (error) {
			Swal.fire({
				icon: "error",
				title: t("ALERT_MESSAGES.ERROR", { defaultValue: "Error" }),
				text:
					error?.message ||
					t("UI_TEXT.BLOCKED_DOMAINS_REMOVE_FAILED", { defaultValue: "Failed to unblock domain" }),
			});
		} finally {
			setBlockedDomainsSaving(false);
		}
	};

	const handleSaveAdminModules = async () => {
		setAdminModulesSaving(true);
		try {
			const resp = await saveChangedOptions([
				{ option_key: "adminEnabledModules", option_value: JSON.stringify(adminEnabledModules) },
			]);
			if (!resp) return;
			if (!resp?.status) {
				throw new Error(resp?.message || "Failed to save admin modules");
			}
			Swal.fire({
				icon: "success",
				title: t("ALERT_MESSAGES.SUCCESS", { defaultValue: "Success" }),
				text: t("LABELS.SETTINGS_UPDATED_SUCCESSFULLY", { defaultValue: "Settings updated successfully" }),
			});
			await fetchOptions();
		} catch (error) {
			Swal.fire({
				icon: "error",
				title: t("ALERT_MESSAGES.ERROR", { defaultValue: "Error" }),
				text: error?.message || t("UI_TEXT.ERROR_SAVING", { defaultValue: "Failed to save changes" }),
			});
		} finally {
			setAdminModulesSaving(false);
		}
	};

	const handleSaveFooterLinks = async () => {
		setFooterSaving(true);
		try {
			const resp = await saveChangedOptions([
				{ option_key: "footerShowVersion", option_value: String(footerShowVersion) },
				{ option_key: "footerShowSupport", option_value: String(footerShowSupport) },
				{ option_key: "footerShowTerms", option_value: String(footerShowTerms) },
				{ option_key: "footerShowEula", option_value: String(footerShowEula) },
				{ option_key: "footerShowPrivacy", option_value: String(footerShowPrivacy) },
				{ option_key: "footerShowCookiePolicy", option_value: String(footerShowCookiePolicy) },
				{ option_key: "footerShowManual", option_value: String(footerShowManual) },
				{ option_key: "footerShowNeedHelp", option_value: String(footerShowNeedHelp) },
			]);
			if (!resp) return;
			if (!resp?.status) {
				throw new Error(resp?.message || "Failed to save footer settings");
			}
			Swal.fire({
				icon: "success",
				title: t("ALERT_MESSAGES.SUCCESS", { defaultValue: "Success" }),
				text: t("LABELS.SETTINGS_UPDATED_SUCCESSFULLY", { defaultValue: "Settings updated successfully" }),
			});
			await fetchOptions();
		} catch (error) {
			Swal.fire({
				icon: "error",
				title: t("ALERT_MESSAGES.ERROR", { defaultValue: "Error" }),
				text: error?.message || t("UI_TEXT.ERROR_SAVING", { defaultValue: "Failed to save changes" }),
			});
		} finally {
			setFooterSaving(false);
		}
	};

	const handleSaveCookieSettings = async () => {
		const parsedDays = Number.parseInt(cookieConsentRePromptDays, 10);
		if (!Number.isFinite(parsedDays) || parsedDays <= 0) {
			Swal.fire({
				icon: "warning",
				title: t("ALERT_MESSAGES.WARNING", { defaultValue: "Warning" }),
				text: t("UI_TEXT.COOKIE_DAYS_REQUIRED", {
					defaultValue: "Please enter a valid number of days greater than zero.",
				}),
			});
			return;
		}
		setCookieSettingsSaving(true);
		try {
			const resp = await saveChangedOptions([
				{ option_key: "cookieConsentRePromptDays", option_value: String(parsedDays) },
			]);
			if (!resp) return;
			if (!resp?.status) {
				throw new Error(resp?.message || "Failed to save cookie settings");
			}
			Swal.fire({
				icon: "success",
				title: t("ALERT_MESSAGES.SUCCESS", { defaultValue: "Success" }),
				text: t("UI_TEXT.SETTINGS_SAVED", { defaultValue: "Settings updated successfully" }),
			});
			await fetchOptions();
		} catch (error) {
			Swal.fire({
				icon: "error",
				title: t("ALERT_MESSAGES.ERROR", { defaultValue: "Error" }),
				text: error?.message || t("UI_TEXT.ERROR_SAVING", { defaultValue: "Failed to save changes" }),
			});
		} finally {
			setCookieSettingsSaving(false);
		}
	};

	/**
	 * Run the STEP sync report on demand — preview, or send for real.
	 *
	 * Exists because the report is otherwise unobservable: it fires once a night
	 * and the not-due paths return silently, so "no email arrived" previously meant
	 * reading the server log. The server returns a diagnostics object whose `reason`
	 * says exactly why a report would or would not go out, and that is what gets
	 * shown here.
	 *
	 * @param {"preview"|"send"} mode Preview marks nothing and emails nobody; send
	 *   emails for real but does not consume the night's scheduled run.
	 * @returns {Promise<void>} Resolves once the round-trip finishes.
	 */
	const handleRunStepSyncReport = async (mode) => {
		if (mode === "send") {
			const confirmed = await Swal.fire({
				icon: "question",
				title: t("ALERT_MESSAGES.ARE_YOU_SURE", { defaultValue: "Are you sure?" }),
				text: t("UI_TEXT.STEP_SYNC_REPORT_SEND_CONFIRM", {
					defaultValue:
						"This emails the report to everyone in the selected roles right now. Tonight's scheduled report still goes out.",
				}),
				showCancelButton: true,
			});
			if (!confirmed.isConfirmed) return;
		}

		setStepSyncReportTesting(mode);
		try {
			const resp = mode === "send" ? await sendStepSyncReportNow() : await previewStepSyncReport();
			if (!resp?.status) {
				throw new Error(resp?.message || "Failed to run the STEP sync report");
			}
			const d = resp.data || {};
			// The counts matter as much as the reason: "0 recipients" and "0 sites
			// settled" are different problems with the same symptom of no email.
			const detail = [
				`${t("LABELS.RECIPIENTS", { defaultValue: "Recipients" })}: ${d.recipients ?? 0}`,
				`${t("LABELS.SITES_REPORTED", { defaultValue: "Sites reported" })}: ${d.sites_reported ?? 0}`,
				`${t("LABELS.EMAILS_SENT", { defaultValue: "Emails sent" })}: ${d.emails_sent ?? 0}`,
			].join(" · ");
			Swal.fire({
				icon: d.emails_sent > 0 || mode === "preview" ? "success" : "info",
				title:
					mode === "send"
						? t("LABELS.STEP_SYNC_REPORT_SENT", { defaultValue: "Report run complete" })
						: t("LABELS.STEP_SYNC_REPORT_PREVIEW", { defaultValue: "Report preview" }),
				text: `${d.reason || ""}

${detail}`,
			});
		} catch (error) {
			Swal.fire({
				icon: "error",
				title: t("ALERT_MESSAGES.ERROR", { defaultValue: "Error" }),
				text: error?.message || t("UI_TEXT.ERROR_SAVING", { defaultValue: "Something went wrong" }),
			});
		} finally {
			setStepSyncReportTesting("");
		}
	};

	/**
	 * Persist the STEP file sync report settings.
	 *
	 * Recipients are roles only — there is no free-text address field any more, so
	 * the old per-address validation is gone with it. A role that currently holds no
	 * active user is still a legitimate save (people get added to roles later); the
	 * server logs it loudly at send time rather than blocking configuration here.
	 *
	 * @returns {Promise<void>} Resolves once the save round-trip and refetch finish.
	 */
	const handleSaveStepSyncReport = async () => {
		// Enabled with no role selected would silently send nothing to nobody.
		if (stepSyncReportEnabled && !stepSyncSuccessRoleIds.length) {
			Swal.fire({
				icon: "warning",
				title: t("ALERT_MESSAGES.WARNING", { defaultValue: "Warning" }),
				text: t("UI_TEXT.STEP_SYNC_REPORT_EMAIL_REQUIRED", {
					defaultValue: "Select at least one recipient role, or turn the report off.",
				}),
			});
			return;
		}

		setStepSyncReportSaving(true);
		try {
			const resp = await saveChangedOptions([
				{ option_key: "stepSyncReportEnabled", option_value: String(stepSyncReportEnabled) },
				{ option_key: "stepSyncReportSuccessRoles", option_value: stepSyncSuccessRoleIds.join(",") },
				{ option_key: "stepSyncReportSendTime", option_value: stepSyncReportSendTime || "05:00" },
			]);
			if (!resp) return;
			if (!resp?.status) {
				throw new Error(resp?.message || "Failed to save STEP sync report settings");
			}
			Swal.fire({
				icon: "success",
				title: t("ALERT_MESSAGES.SUCCESS", { defaultValue: "Success" }),
				text: t("LABELS.SETTINGS_UPDATED_SUCCESSFULLY", { defaultValue: "Settings updated successfully" }),
			});
			await fetchOptions();
		} catch (error) {
			Swal.fire({
				icon: "error",
				title: t("ALERT_MESSAGES.ERROR", { defaultValue: "Error" }),
				text: error?.message || t("UI_TEXT.ERROR_SAVING", { defaultValue: "Failed to save changes" }),
			});
		} finally {
			setStepSyncReportSaving(false);
		}
	};

	/**
	 * Persist who receives the "site cannot be priced" alert.
	 *
	 * Two options, both in the `options` table: a JSON array of role ids and a
	 * boolean flag. Roles are stored as ids rather than names because a role can
	 * be renamed in Manage Roles without the recipient list silently emptying.
	 *
	 * Saving an empty role list with the CCR flag off is allowed and means the
	 * server falls back to its default admin audience -- it is not a way to
	 * switch the alert off, so it warns rather than blocks.
	 */
	const handleSaveMissingSiteConfigSettings = async () => {
		if (missingSiteConfigRoles.length === 0 && !missingSiteConfigUseCcr) {
			const confirmation = await Swal.fire({
				icon: "warning",
				title: t("ALERT_MESSAGES.WARNING", { defaultValue: "Warning" }),
				text: t("UI_TEXT.MISSING_SITE_CONFIG_NO_RECIPIENTS", {
					defaultValue:
						"No roles are selected and the CCR option is off, so this alert falls back to all admins. Save anyway?",
				}),
				showCancelButton: true,
				confirmButtonText: t("BUTTON.YES", { defaultValue: "Yes" }),
				cancelButtonText: t("BUTTON.CANCEL", { defaultValue: "Cancel" }),
			});
			if (!confirmation.isConfirmed) return;
		}
		setMissingSiteConfigSaving(true);
		try {
			const resp = await saveChangedOptions([
				{
					option_key: "missingSiteConfigNotifyRoles",
					option_value: JSON.stringify(missingSiteConfigRoles),
				},
				{
					option_key: "missingSiteConfigNotifyCcr",
					option_value: String(missingSiteConfigUseCcr),
				},
			]);
			if (!resp) return;
			if (!resp?.status) {
				throw new Error(resp?.message || "Failed to save notification recipients");
			}
			Swal.fire({
				icon: "success",
				title: t("ALERT_MESSAGES.SUCCESS", { defaultValue: "Success" }),
				text: t("UI_TEXT.SETTINGS_SAVED", { defaultValue: "Settings updated successfully" }),
			});
			await fetchOptions();
		} catch (error) {
			Swal.fire({
				icon: "error",
				title: t("ALERT_MESSAGES.ERROR", { defaultValue: "Error" }),
				text: error?.message || t("UI_TEXT.ERROR_SAVING", { defaultValue: "Failed to save changes" }),
			});
		} finally {
			setMissingSiteConfigSaving(false);
		}
	};

	const addCustomerLocationServiceRow = () => {
		setCustomerLocationServiceRows((prev) => [
			...prev,
			{ key: "", label: "", description: "", href: "" },
		]);
	};

	const updateCustomerLocationServiceRow = (index, field, value) => {
		setCustomerLocationServiceRows((prev) =>
			prev.map((row, idx) => (idx === index ? { ...row, [field]: value } : row))
		);
	};

	const updateEmergencyPhone = (region, phone) => {
		setCustomerLocationEmergencyPhones((prev) => {
			const next = Array.isArray(prev) ? [...prev] : [];
			const idx = next.findIndex((row) => row.region === region);
			if (idx >= 0) {
				next[idx] = { ...next[idx], phone };
			} else {
				next.push({
					region,
					iso2: REGION_DEFAULT_ISO2[region] || "US",
					phone,
				});
			}
			return next;
		});
	};

	const updateEmergencyPhoneCountry = (region, iso2) => {
		const nextIso2 = String(iso2 || REGION_DEFAULT_ISO2[region] || "US").toUpperCase();
		const selectedCountry = phoneCountries.find((country) => country.code === nextIso2);
		setCustomerLocationEmergencyPhones((prev) => {
			const next = Array.isArray(prev) ? [...prev] : [];
			const idx = next.findIndex((row) => row.region === region);
			const existing = idx >= 0 ? next[idx] : { region, phone: "" };
			const phone = replacePhoneDialCode(existing.phone, selectedCountry?.phone || "");
			const updated = { ...existing, region, iso2: nextIso2, phone };
			if (idx >= 0) {
				next[idx] = updated;
			} else {
				next.push(updated);
			}
			return next;
		});
	};

	const updateSupportCaseTypeOption = (index, field, value) => {
		setSupportCaseTypeConfig((prev) => ({
			...prev,
			options: (prev.options || []).map((option, idx) => (idx === index ? { ...option, [field]: value } : option)),
		}));
	};

	const addSupportCaseTypeOption = () => {
		setSupportCaseTypeConfig((prev) => ({
			...prev,
			options: [...(prev.options || []), { value: "", label: "" }],
		}));
	};

	const removeSupportCaseTypeOption = (index) => {
		setSupportCaseTypeConfig((prev) => ({
			...prev,
			options: (prev.options || []).filter((_, idx) => idx !== index),
		}));
	};

	const updateSupportCaseSafetyOption = (index, field, value) => {
		setSupportCaseSafetyConfig((prev) => ({
			...prev,
			options: (prev.options || []).map((option, idx) => (idx === index ? { ...option, [field]: value } : option)),
		}));
	};

	const addSupportCaseSafetyOption = () => {
		setSupportCaseSafetyConfig((prev) => ({
			...prev,
			options: [...(prev.options || []), { value: "", label: "" }],
		}));
	};

	const removeSupportCaseSafetyOption = (index) => {
		setSupportCaseSafetyConfig((prev) => ({
			...prev,
			options: (prev.options || []).filter((_, idx) => idx !== index),
		}));
	};

	const removeCustomerLocationServiceRow = (index) => {
		setCustomerLocationServiceRows((prev) => prev.filter((_, idx) => idx !== index));
	};

	const validateCustomerLocationServices = () => {
		const normalized = customerLocationServiceRows.map((row) => ({
			key: String(row.key || "").trim(),
			label: String(row.label || "").trim(),
		}));
		const missing = normalized.some((row) => !row.key || !row.label);
		if (missing) {
			setCustomerLocationServiceError("Each service requires a key and label.");
			return false;
		}
		const keys = normalized.map((row) => row.key);
		const hasDupes = new Set(keys).size !== keys.length;
		if (hasDupes) {
			setCustomerLocationServiceError("Service keys must be unique.");
			return false;
		}
		setCustomerLocationServiceError("");
		return true;
	};

	const handleSaveCustomerLocationSettings = async () => {
		if (customerLocationServicesEnabled && !validateCustomerLocationServices()) {
			return;
		}
		setCustomerLocationSaving(true);
		try {
			const servicesPayload = serializeServiceOptions(customerLocationServiceRows);
			const payload = [
				{ option_key: "customerLocationServicesEnabled", option_value: String(customerLocationServicesEnabled) },
				{ option_key: "customerLocationServices", option_value: servicesPayload },
				{ option_key: "customerLocationShowLegalEntity", option_value: String(customerLocationShowLegalEntity) },
				{ option_key: "customerLocationShowSite", option_value: String(customerLocationShowSite) },
				{ option_key: "customerLocationShowWarehouse", option_value: String(customerLocationShowWarehouse) },
				{ option_key: "customerLocationShowPaymentTerms", option_value: String(customerLocationShowPaymentTerms) },
				{ option_key: "customerLocationShowOnHold", option_value: String(customerLocationShowOnHold) },
				{ option_key: "customerLocationEmergencyPhones", option_value: JSON.stringify(customerLocationEmergencyPhones || []) },
			];
			const resp = await saveChangedOptions(payload);
			if (!resp) return;
			if (!resp?.status) {
				throw new Error(resp?.message || "Failed to save customer location settings");
			}
			Swal.fire({
				icon: "success",
				title: t("ALERT_MESSAGES.SUCCESS", { defaultValue: "Success" }),
				text: t("LABELS.SETTINGS_UPDATED_SUCCESSFULLY", { defaultValue: "Settings updated successfully" }),
			});
			await fetchOptions();
		} catch (error) {
			Swal.fire({
				icon: "error",
				title: t("ALERT_MESSAGES.ERROR", { defaultValue: "Error" }),
				text: error?.message || t("UI_TEXT.ERROR_SAVING", { defaultValue: "Failed to save changes" }),
			});
		} finally {
			setCustomerLocationSaving(false);
		}
	};

	const validateSupportOptionsConfig = () => {
		const normalize = (list) =>
			(Array.isArray(list) ? list : [])
				.map((item) => ({
					value: String(item?.value || "").trim(),
					label: String(item?.label || "").trim(),
				}))
				.filter((item) => item.value || item.label);

		const caseTypes = normalize(supportCaseTypeConfig.options);
		if (!caseTypes.length || caseTypes.some((item) => !item.value || !item.label)) return "Case Type options must include value and label.";
		if (new Set(caseTypes.map((item) => item.value)).size !== caseTypes.length) return "Case Type values must be unique.";

		const caseSafety = normalize(supportCaseSafetyConfig.options);
		if (!caseSafety.length || caseSafety.some((item) => !item.value || !item.label)) return "Case Safety options must include value and label.";
		if (new Set(caseSafety.map((item) => item.value)).size !== caseSafety.length) return "Case Safety values must be unique.";

		if (supportContactCcrEmergencyNumberSource === "manual" && !isValidEmergencyPhone(supportContactCcrEmergencyNumberManual)) {
			return "Contact CCR emergency number format must be +countrycode 10digits.";
		}
		if (supportCreateCaseEmergencyNumberSource === "manual" && !isValidEmergencyPhone(supportCreateCaseEmergencyNumberManual)) {
			return "Create Case emergency number format must be +countrycode 10digits.";
		}
		return null;
	};

	const handleSaveSupportInformation = async () => {
		const validationError = validateSupportOptionsConfig();
		if (validationError) {
			Swal.fire({
				icon: "warning",
				title: t("ALERT_MESSAGES.WARNING", { defaultValue: "Warning" }),
				text: validationError,
			});
			return;
		}
		setSupportInfoSaving(true);
		try {
			const payload = [
				{ option_key: "supportContactCcrShowEmergencyMessage", option_value: String(supportContactCcrShowEmergencyMessage) },
				{ option_key: "supportContactCcrEmergencyNumberSource", option_value: supportContactCcrEmergencyNumberSource },
				{ option_key: "supportContactCcrEmergencyNumberManual", option_value: supportContactCcrEmergencyNumberManual.trim() },
				{ option_key: "supportCreateCaseShowEmergencyMessage", option_value: String(supportCreateCaseShowEmergencyMessage) },
				{ option_key: "supportCreateCaseEmergencyNumberSource", option_value: supportCreateCaseEmergencyNumberSource },
				{ option_key: "supportCreateCaseEmergencyNumberManual", option_value: supportCreateCaseEmergencyNumberManual.trim() },
				{ option_key: "supportEnableErpCaseCreation", option_value: String(supportEnableErpCaseCreation) },
				{ option_key: "supportCreateCaseFieldVisibility", option_value: JSON.stringify(supportCreateCaseFieldVisibility || DEFAULT_SUPPORT_SETTINGS.createCaseFieldVisibility) },
				{ option_key: "supportCreateCaseTypeConfig", option_value: JSON.stringify(supportCaseTypeConfig) },
				{ option_key: "supportCreateCaseSafetyConfig", option_value: JSON.stringify(supportCaseSafetyConfig) },
			];
			const resp = await saveChangedOptions(payload);
			if (!resp) return;
			if (!resp?.status) throw new Error(resp?.message || "Failed to save support settings");
			Swal.fire({
				icon: "success",
				title: t("ALERT_MESSAGES.SUCCESS", { defaultValue: "Success" }),
				text: t("LABELS.SETTINGS_UPDATED_SUCCESSFULLY", { defaultValue: "Settings updated successfully" }),
			});
			await fetchOptions();
		} catch (error) {
			Swal.fire({
				icon: "error",
				title: t("ALERT_MESSAGES.ERROR", { defaultValue: "Error" }),
				text: error?.message || t("UI_TEXT.ERROR_SAVING", { defaultValue: "Failed to save changes" }),
			});
		} finally {
			setSupportInfoSaving(false);
		}
	};

	const toggleAdminModule = (key) => {
		setAdminEnabledModules((prev) =>
			prev.includes(key) ? prev.filter((item) => item !== key) : [...prev, key]
		);
	};

	const handleSaveRsplSettings = async () => {
		const nextList = rowsToRsplList(rsplRows);
		if (!nextList.length) {
			setRsplError(
				t("UI_TEXT.INVALID_RSPL_JSON", {
					defaultValue: "Enter at least one valid key/value pair.",
				})
			);
			return;
		}
		setRsplError("");
		setRsplSaving(true);
		try {
			const resp = await saveChangedOptions([{ option_key: "rsplList", option_value: JSON.stringify(nextList) }]);
			if (!resp) return;
			if (!resp?.status) {
				throw new Error(resp?.message || "Failed to save RSPL settings");
			}
			Swal.fire({
				icon: "success",
				title: t("ALERT_MESSAGES.SUCCESS", { defaultValue: "Success" }),
				text: t("LABELS.SETTINGS_UPDATED_SUCCESSFULLY", { defaultValue: "Settings updated successfully" }),
			});
			await fetchOptions();
		} catch (error) {
			Swal.fire({
				icon: "error",
				title: t("ALERT_MESSAGES.ERROR", { defaultValue: "Error" }),
				text: error?.message || t("UI_TEXT.ERROR_SAVING", { defaultValue: "Failed to save changes" }),
			});
		} finally {
			setRsplSaving(false);
		}
	};

	const handleSaveModelSettings = async () => {
		const orientationValues = [
			modelOrientationUp,
			modelOrientationLeft,
			modelOrientationRight,
		].map((axis) => normalizeModelAxis(axis, ""));
		const isOrientationValid =
			orientationValues.every((axis) => MODEL_AXIS_CHOICES.includes(axis)) &&
			new Set(orientationValues).size === 3;
		if (!isOrientationValid) {
			Swal.fire({
				icon: "warning",
				title: t("ALERT_MESSAGES.WARNING", { defaultValue: "Warning" }),
				text: t("UI_TEXT.MODEL_ORIENTATION_AXES_MUST_BE_UNIQUE", {
					defaultValue: "Choose unique axes for Up, Left, and Right.",
				}),
			});
			return;
		}

		setModelAxisSaving(true);
		try {
			const resp = await saveChangedOptions([
				{ option_key: "modelViewerUpAxis", option_value: `${modelOrientationUp}_UP` },
				{ option_key: "modelViewerOrientationUp", option_value: modelOrientationUp },
				{ option_key: "modelViewerOrientationLeft", option_value: modelOrientationLeft },
				{ option_key: "modelViewerOrientationRight", option_value: modelOrientationRight },
				{ option_key: "modelViewerDefaultCameraCorner", option_value: modelDefaultCameraCorner },
				{
					option_key: "modelViewerLightIntensity",
					option_value: String(modelLightIntensity),
				},
				{
					option_key: "modelViewerHighlightColor",
					option_value: modelHighlightColor,
				},
				{
					option_key: "modelViewerHighlightOpacity",
					option_value: String(modelHighlightOpacity),
				},
				{
					option_key: "modelViewerTabTitleSubAssemblies",
					option_value: modelMenuTabSubassemblies,
				},
				{
					option_key: "modelViewerTabTitleRspl",
					option_value: modelMenuTabRspl,
				},
				{
					option_key: "modelViewerTabTitlePartsTree",
					option_value: modelMenuTabPartsTree,
				},
				{
					option_key: "modelViewerShowTabSubtitles",
					option_value: String(modelMenuShowSubtitles),
				},
			]);
			if (!resp) return;
			if (!resp?.status) {
				throw new Error(resp?.message || "Failed to save 3D model settings");
			}
			Swal.fire({
				icon: "success",
				title: t("ALERT_MESSAGES.SUCCESS", { defaultValue: "Success" }),
				text: t("LABELS.SETTINGS_UPDATED_SUCCESSFULLY", { defaultValue: "Settings updated successfully" }),
			});
			await fetchOptions();
		} catch (error) {
			Swal.fire({
				icon: "error",
				title: t("ALERT_MESSAGES.ERROR", { defaultValue: "Error" }),
				text: error?.message || t("UI_TEXT.ERROR_SAVING", { defaultValue: "Failed to save changes" }),
			});
		} finally {
			setModelAxisSaving(false);
		}
	};

	const updateRsplRow = (index, field, value) => {
		setRsplRows((prev) =>
			prev.map((row, i) => (i === index ? { ...row, [field]: value } : row))
		);
		if (rsplError) setRsplError("");
	};

	const addRsplRow = () => {
		setRsplRows((prev) => [...prev, { key: "", value: "" }]);
		if (rsplError) setRsplError("");
	};

	const removeRsplRow = (index) => {
		setRsplRows((prev) => prev.filter((_, i) => i !== index));
	};

	const updateOrderStatusMapping = (index, field, value) => {
		setOrderStatusMappings((prev) =>
			prev.map((row, idx) => (idx === index ? { ...row, [field]: value } : row))
		);
	};

	const addOrderStatusMapping = () => {
		setOrderStatusMappings((prev) => [...prev, { backendStatus: "", label: "", showInTracker: true }]);
	};

	const removeOrderStatusMapping = (index) => {
		setOrderStatusMappings((prev) => prev.filter((_, idx) => idx !== index));
	};

	const handleSaveSiteSelection = async () => {
		setSiteSaving(true);
		const payload = [
			{ option_key: "siteSelectionGroupingEnabled", option_value: String(siteGroupingEnabled) },
			{ option_key: "siteSelectionShowSiteNumber", option_value: String(siteNumberVisible) },
		];
		try {
			const resp = await saveChangedOptions(payload);
			if (!resp) return;
			if (!resp?.status) {
				throw new Error(resp?.message || "Failed to save site selection settings");
			}
			Swal.fire({
				icon: "success",
				title: t("ALERT_MESSAGES.SUCCESS", { defaultValue: "Success" }),
				text: t("LABELS.SETTINGS_UPDATED_SUCCESSFULLY", { defaultValue: "Settings updated successfully" }),
			});
			await fetchOptions();
		} catch (error) {
			Swal.fire({
				icon: "error",
				title: t("ALERT_MESSAGES.ERROR", { defaultValue: "Error" }),
				text: error?.message || t("UI_TEXT.ERROR_SAVING", { defaultValue: "Failed to save changes" }),
			});
		} finally {
			setSiteSaving(false);
		}
	};

	const previewColor = useMemo(() => {
		if (themeColors[selectedColor]) return themeColors[selectedColor].main;
		if (isValidHex(selectedColor)) return selectedColor;
		return undefined;
	}, [selectedColor]);
	const themeTabIndex = 0;
	const platformTabIndex = 1;
	const footerTabIndex = 2;
	const siteSelectionTabIndex = 3;
	const rsplTabIndex = 4;
	const model3dTabIndex = 5;
	const customerLocationTabIndex = 6;
	const supportInfoTabIndex = 7;
	const adminModulesTabIndex = 8;
	const cookieTabIndex = 9;
	const stepSyncReportTabIndex = 10;
	const missingSiteConfigTabIndex = 11;
	const publicRegistrationTabIndex = 12;

	useEffect(() => {
		const maxTab = isSuperAdmin ? publicRegistrationTabIndex : themeTabIndex;
		if (tab > maxTab) {
			setTab(themeTabIndex);
		}
	}, [tab, isSuperAdmin, publicRegistrationTabIndex, themeTabIndex]);

	// Roles are only needed by the super-admin-only recipients tab, so they are
	// fetched once here rather than with the rest of the settings payload.
	useEffect(() => {
		if (!isSuperAdmin) return;
		let cancelled = false;
		(async () => {
			try {
				const resp = await getRoles();
				// getRoles returns {data: {roles: []}} in some builds and {data: []}
				// in others -- CreateEditUserForm handles both, so do the same here.
				const payload = Array.isArray(resp?.data?.roles)
					? resp.data.roles
					: Array.isArray(resp?.data)
						? resp.data
						: [];
				if (!cancelled) setRoleOptions(payload);
			} catch {
				if (!cancelled) setRoleOptions([]);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [isSuperAdmin]);

	// This page edits the stored options, so it re-reads them on mount instead of
	// trusting whatever the provider happened to load at app start -- a fetch that
	// failed back then left the forms showing blank defaults that a Save would
	// then write over the real values. fetchOptions is deliberately not a
	// dependency: the provider rebuilds it every render, which would loop.
	useEffect(() => {
		fetchOptions();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// The blocked-domain list is super-admin-only, like the section that shows it.
	useEffect(() => {
		if (!isSuperAdmin) return;
		loadBlockedDomains();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isSuperAdmin]);

	// One gate for the whole page: no form renders, and therefore no Save button
	// exists, until the stored options are actually in hand. The error branch is
	// checked first because fetchOptions clears optionsError when a retry starts,
	// so a retry in flight falls through to the loader on its own.
	if (optionsError) {
		return (
			<Box sx={{ p: 2 }}>
				<Alert
					severity="error"
					action={
						<Button color="inherit" size="small" onClick={() => fetchOptions()}>
							{t("BUTTON.RETRY", { defaultValue: "Retry" })}
						</Button>
					}
				>
					{optionsError}
				</Alert>
			</Box>
		);
	}

	// Only the first load blanks the page. Every successful save calls fetchOptions() again, and
	// gating on optionsLoading as well would flash a full-page spinner after each save; the stored
	// values are already in hand by then, so the forms can stay on screen.
	if (!optionsLoaded) {
		return (
			<Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
				<CircularProgress />
			</Box>
		);
	}

	return (
		<>
			<Box>

				<Tabs
					value={tab}
					onChange={(_, value) => setTab(value)}
					variant="scrollable"
					scrollButtons="auto"
					sx={{ borderBottom: "1px solid #ddd", mb: 3 }}
				>
					<Tab label={t("UI_TEXT.THEME", { defaultValue: "Theme" })} />
					{isSuperAdmin && (
						<Tab label={t("LABELS.PLATFORM_SETTINGS", { defaultValue: "Platform Settings" })} />
					)}
					{isSuperAdmin && (
						<Tab label={t("LABELS.FOOTER_LINKS", { defaultValue: "Footer Links" })} />
					)}
					{isSuperAdmin && (
						<Tab label={t("LABELS.SITE_SELECTION_SETTINGS", { defaultValue: "Site Selection Settings" })} />
					)}
					{isSuperAdmin && (
						<Tab label={t("LABELS.RSPL_SETTINGS", { defaultValue: "RSPL Settings" })} />
					)}
					{isSuperAdmin && (
						<Tab label={t("LABELS.MODEL_3D_SETTINGS", { defaultValue: "3D Model Settings" })} />
					)}
					{isSuperAdmin && (
						<Tab label={t("LABELS.CUSTOMER_LOCATION_SETTINGS", { defaultValue: "Customer Location Settings" })} />
					)}
					{isSuperAdmin && (
						<Tab label={t("UI_TEXT.SUPPORT_INFORMATION", { defaultValue: "Support Information" })} />
					)}
					{isSuperAdmin && (
						<Tab label={t("LABELS.ADMIN_MODULE_AVAILABILITY", { defaultValue: "Admin Module Availability" })} />
					)}
					{isSuperAdmin && (
						<Tab label={t("UI_TEXT.COOKIE_SETTINGS", { defaultValue: "Cookie Settings" })} />
					)}
					{isSuperAdmin && (
						<Tab label={t("LABELS.STEP_SYNC_REPORTS", { defaultValue: "STEP File Sync Reports" })} />
					)}
					{isSuperAdmin && (
						<Tab label={t("LABELS.MISSING_SITE_CONFIG_ROLE", { defaultValue: "Missing Site Config Role" })} />
					)}
					{isSuperAdmin && (
						<Tab label={t("UI_TEXT.PUBLIC_REGISTRATION_SETTINGS", { defaultValue: "Public Registration Settings" })} />
					)}
				</Tabs>

				{tab === themeTabIndex && (
					<>
						<Box
							sx={{
								border: "1px solid",
								borderColor: "divider",
								borderRadius: 2,
								p: 2,
								mb: 3,
							}}
						>
							<Typography variant="body1" fontWeight={"bold"}>
								{t("UI_TEXT.SELECT_ACCENT_COLOR", { defaultValue: "Select Accent Color" })}
							</Typography>
							<Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', width: '100%', columnGap: 2 }}>
								<Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-start', flexDirection: 'column', width: '100%', rowGap: 2 }}>

									<Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 2 }}>
										{presetColors.map(({ key, palette }) => (
											<Button
												key={key}
												variant={selectedColor === key ? "contained" : "outlined"}
												onClick={() => handleSelectAccent(key)}
												sx={{
													minWidth: 42,
													width: 42,
													height: 42,
													borderRadius: "50%",
													borderColor: palette.main,
													backgroundColor: palette.main,
													color: "#fff",
													opacity: selectedColor === key ? 1 : 0.7,
												}}
											>
												{" "}
											</Button>
										))}
									</Stack>
									<Divider flexItem>{t('LABELS.OR')}</Divider>
									<TextField
										label={t("LABELS.CUSTOM", { defaultValue: "Custom" })}
										placeholder="#0066FF"
										value={customColor}
										onChange={(e) => handleCustomColorChange(e.target.value)}
										size="small"
										fullWidth
										error={!!customColorError}
										helperText={customColorError || t("LABELS.ENTER_A_HEX_VALUE_TO_SET_A_CUSTOM_ACCENT_COLOR", {
											defaultValue: "Enter a hex value to set a custom accent color",
										})}
									/>
								</Box>
								{previewColor && (
									<>
										<Divider orientation="vertical" flexItem />

										<Box
											sx={{
												minWidth: '120px',
												height: '100%',
												minHeight: '120px',
												width: '120px',
												borderRadius: '15px',
												border: "1px solid",
												borderColor: "divider",
												backgroundColor: previewColor,
												display: 'flex',
												justifyContent: 'center',
												alignItems: 'center'
											}}
										>
											<Typography variant="body1" color="primary.contrastText">{t("LABELS.SITE_SELECTION_SETTINGS")}</Typography>
										</Box>
									</>
								)}
							</Box>
						</Box>
						<Box
							sx={{
								border: "1px solid",
								borderColor: "divider",
								borderRadius: 2,
								p: 2,
								mb: 3,
							}}
						>
							<Typography variant="body1" sx={{ mb: 2 }} fontWeight={"bold"}>
								{t("LABELS.HEADER_AND_BUTTON_FONT", { defaultValue: "Header & Button Font" })}
							</Typography>
							<FormControl fullWidth size="small" sx={{ mb: 2 }}>
								<InputLabel>{t("LABELS.CHOOSE_FONT", { defaultValue: "Choose Font" })}</InputLabel>
								<Select
									value={headerFontKey}
									label={t("LABELS.CHOOSE_FONT", { defaultValue: "Choose Font" })}
									onChange={(e) => setHeaderFontKey(e.target.value)}
								>
									{Object.keys(fonts).map((font) => (
										<MenuItem key={font} value={font}>
											<span style={{ fontFamily: fonts[font] }}>{font}</span>
										</MenuItem>
									))}
								</Select>
							</FormControl>
							<FormControl fullWidth size="small">
								<InputLabel>{t("LABELS.CHOOSE_WEIGHT", { defaultValue: "Choose Weight" })}</InputLabel>
								<Select
									value={headerFontWeight === null ? "" : String(headerFontWeight)}
									label={t("LABELS.CHOOSE_WEIGHT", { defaultValue: "Choose Weight" })}
									onChange={(e) =>
										setHeaderFontWeight(e.target.value ? Number.parseInt(e.target.value, 10) : null)
									}
								>
									{fontWeightOptions.map((option) => (
										<MenuItem key={option.value || "default"} value={option.value}>
											{option.label}
										</MenuItem>
									))}
								</Select>
							</FormControl>

							<Typography variant="body1" sx={{ my: 2 }} fontWeight={"bold"}>
								{t("LABELS.BODY_TYPOGRAPHY", { defaultValue: "Body Typography" })}
							</Typography>
							<FormControl fullWidth size="small">
								<InputLabel>{t("LABELS.CHOOSE_FONT", { defaultValue: "Choose Font" })}</InputLabel>
								<Select
									value={bodyFontKey}
									label={t("LABELS.CHOOSE_FONT", { defaultValue: "Choose Font" })}
									onChange={(e) => setBodyFontKey(e.target.value)}
								>
									{Object.keys(fonts).map((font) => (
										<MenuItem key={font} value={font}>
											<span style={{ fontFamily: fonts[font] }}>{font}</span>
										</MenuItem>
									))}
								</Select>
							</FormControl>
							<FormControl fullWidth size="small" sx={{ mt: 2 }}>
								<InputLabel >{t("LABELS.CHOOSE_WEIGHT", { defaultValue: "Choose Weight" })}</InputLabel>
								<Select
									value={bodyFontWeight === null ? "" : String(bodyFontWeight)}
									label={t("LABELS.CHOOSE_WEIGHT", { defaultValue: "Choose Weight" })}
									onChange={(e) =>
										setBodyFontWeight(e.target.value ? Number.parseInt(e.target.value, 10) : null)
									}
								>
									{fontWeightOptions.map((option) => (
										<MenuItem key={option.value || "default"} value={option.value}>
											{option.label}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						</Box>
						<Box textAlign="right" sx={{ mt: 3 }}>
							<Button
								variant="contained"
								onClick={handleSaveTheme}
								startIcon={<Save />}
								disabled={saving || !!customColorError}
							>
								{saving
									? t("BUTTON.SAVING", { defaultValue: "Saving..." })
									: t("BUTTON.SAVE_CHANGES", { defaultValue: "Save Theme" })}
							</Button>
						</Box>
					</>
				)}

				{isSuperAdmin && tab === platformTabIndex && (
					<Box
						sx={{
							border: "1px solid",
							borderColor: "divider",
							borderRadius: 2,
							p: 2,
							mb: 3,
						}}
					>
						<Typography variant="body1" sx={{ mb: 2 }} fontWeight={"bold"}>
							{t("UI_TEXT.PLATFORM_SETTINGS", { defaultValue: "Platform Settings" })}
						</Typography>
						<FormGroup>
							<FormControlLabel
								control={
									<Switch
										checked={enableTours}
										onChange={(e) => setEnableTours(e.target.checked)}
									/>
								}
								label={t("LABELS.ENABLE_TOURS", { defaultValue: "Enable Tours" })}
							/>
							<FormControlLabel
								control={
									<Switch
										checked={enableChatbot}
										onChange={(e) => setEnableChatbot(e.target.checked)}
									/>
								}
								label={t("LABELS.ENABLE_CHATBOT", { defaultValue: "Enable Chatbot" })}
							/>
							<FormControlLabel
								control={
									<Switch
										checked={enableLanguageTranslation}
										onChange={(e) => setEnableLanguageTranslation(e.target.checked)}
									/>
								}
								label={t("LABELS.ENABLE_LANGUAGE_TRANSLATION", {
									defaultValue: "Enable Language Translation",
								})}
							/>
						</FormGroup>
						<FormControl fullWidth size="small" sx={{ mt: 2 }}>
							<InputLabel>{t("LABELS.DEFAULT_LANGUAGE", { defaultValue: "Default Language" })}</InputLabel>
							<Select
								value={defaultLanguage}
								label={t("LABELS.DEFAULT_LANGUAGE", { defaultValue: "Default Language" })}
								disabled={enableLanguageTranslation}
								onChange={(e) => setDefaultLanguage(e.target.value)}
							>
								{languageOptions.map((lang) => (
									<MenuItem key={lang.value} value={lang.value}>
										{lang.label}
									</MenuItem>
								))}
							</Select>
						</FormControl>
						<Divider sx={{ my: 2 }} />
						<Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
							{t("UI_TEXT.ORDER_HISTORY_PRICE_VISIBILITY", { defaultValue: "Order History Price Visibility" })}
						</Typography>
						<FormGroup>
							<FormControlLabel
								control={
									<Switch
										checked={showOrderHistoryPricesAll}
										onChange={(e) => {
											const checked = e.target.checked;
											setShowOrderHistoryPricesAll(checked);
											if (!checked) setShowOrderHistoryPricesPast(false);
										}}
									/>
								}
								label={t("LABELS.SHOW_PRICES_ALL_ORDERS", { defaultValue: "Show prices for all orders" })}
							/>
							<FormControlLabel
								control={
									<Switch
										checked={showOrderHistoryPricesPast}
										onChange={(e) => setShowOrderHistoryPricesPast(e.target.checked)}
										disabled={!showOrderHistoryPricesAll}
									/>
								}
								label={t("LABELS.SHOW_PRICES_PAST_ORDERS", { defaultValue: "Show prices for past orders" })}
							/>
						</FormGroup>
						<Divider sx={{ my: 2 }} />
						<Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
							{t("UI_TEXT.CHECKOUT_SETTINGS", { defaultValue: "Checkout Settings" })}
						</Typography>
						<FormControlLabel
							control={
								<Switch
									checked={enableOrderSchedules}
									onChange={(e) => setEnableOrderSchedules(e.target.checked)}
								/>
							}
							label={t("LABELS.ENABLE_ORDER_SCHEDULES", {
								defaultValue: "Enable order schedules across Recommendations, Checkout, and Order History",
							})}
						/>
						<FormControlLabel
							control={
								<Switch
									checked={enableCheckoutAddressEditing}
									onChange={(e) => setEnableCheckoutAddressEditing(e.target.checked)}
								/>
							}
							label={t("LABELS.ENABLE_CHECKOUT_ADDRESS_EDITING", { defaultValue: "Enable address editing on checkout and customer locations" })}
						/>
						<FormControlLabel
							control={
								<Switch
									checked={enableMachineSellNotifyForm}
									onChange={(e) => setEnableMachineSellNotifyForm(e.target.checked)}
								/>
							}
							label={t("LABELS.ENABLE_MACHINE_SOLD_NOTIFICATION_UI", { defaultValue: "Enable machine sold notification UI" })}
						/>
						<Divider sx={{ my: 2 }} />
						<Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
							{t("UI_TEXT.PRODUCT_FILTER_DEFAULTS", { defaultValue: "Product Filter Defaults" })}
						</Typography>
						<FormControl fullWidth size="small" sx={{ mb: 2 }}>
							<InputLabel>
								{t("UI_TEXT.DEFAULT_PRODUCT_SORT", { defaultValue: "Default Product Sort" })}
							</InputLabel>
							<Select
								value={productsDefaultSortKey}
								label={t("UI_TEXT.DEFAULT_PRODUCT_SORT", { defaultValue: "Default Product Sort" })}
								onChange={(e) => setProductsDefaultSortKey(String(e.target.value || "price_desc"))}
							>
								{PRODUCT_DEFAULT_SORT_OPTIONS.map((option) => (
									<MenuItem key={option.key} value={option.key}>
										{t(`GENERAL.${option.label.toUpperCase().replace(/\s+/g, "_")}`, { defaultValue: option.label })}
									</MenuItem>
								))}
							</Select>
						</FormControl>
						<FormControl fullWidth size="small">
							<InputLabel>
								{t("UI_TEXT.DEFAULT_PRODUCT_FILTERS", { defaultValue: "Default Product Filters" })}
							</InputLabel>
							<Select
								multiple
								value={productsDefaultFilterTypes}
								label={t("UI_TEXT.DEFAULT_PRODUCT_FILTERS", { defaultValue: "Default Product Filters" })}
								onChange={(e) => {
									const nextValue = Array.isArray(e.target.value) ? e.target.value : [e.target.value];
									const hadAllSelected = productsDefaultFilterTypes.includes("__ALL__");
									const hasAllInNext = nextValue.includes("__ALL__");
									const normalized = nextValue
										.map((value) => String(value || "").trim())
										.filter(Boolean)
										.filter((value) => value !== "__ALL__");
									// If "All" was previously selected and the user chooses specific options,
									// switch to those options instead of forcing "__ALL__".
									if (hadAllSelected && hasAllInNext && normalized.length > 0) {
										setProductsDefaultFilterTypes(Array.from(new Set(normalized)));
										return;
									}
									if (hasAllInNext) {
										setProductsDefaultFilterTypes(["__ALL__"]);
										return;
									}
									setProductsDefaultFilterTypes(
										normalized.length > 0 ? Array.from(new Set(normalized)) : ["__ALL__"]
									);
								}}
								renderValue={(selected) => {
									if (!Array.isArray(selected) || selected.includes("__ALL__")) {
										return t("UI_TEXT.ALL_PART_TYPES", { defaultValue: "All Part Types" });
									}
									return selected.join(", ");
								}}
							>
								<MenuItem value="__ALL__">
									<Checkbox checked={productsDefaultFilterTypes.includes("__ALL__")} size="small" />
									{t("UI_TEXT.ALL_PART_TYPES", { defaultValue: "All Part Types" })}
								</MenuItem>
								{productDefaultFilterOptions.map((option) => (
									<MenuItem key={option} value={option}>
										<Checkbox
											checked={
												!productsDefaultFilterTypes.includes("__ALL__") &&
												productsDefaultFilterTypes.includes(option)
											}
											size="small"
										/>
										{option}
									</MenuItem>
								))}
							</Select>
						</FormControl>
						<Divider sx={{ my: 2 }} />
						<Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
							{t("UI_TEXT.ORDER_TRACKER_SETTINGS", { defaultValue: "Order Tracker Settings" })}
						</Typography>
						<TextField
							label={t("LABELS.ORDER_STATUS_LABELS", { defaultValue: "Order Status Labels (comma separated)" })}
							value={orderStatusLabels}
							onChange={(e) => setOrderStatusLabels(e.target.value)}
							size="small"
							fullWidth
							multiline
							minRows={2}
							helperText={t("UI_TEXT.ORDER_STATUS_LABELS_HELPER", {
								defaultValue: "Example: Pending, Open Order, Shipped, Delivered, Invoiced, Canceled",
							})}
						/>
						<Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
							{t("UI_TEXT.ORDER_STATUS_MAPPING", {
								defaultValue: "Backend to frontend status mapping",
							})}
						</Typography>
						<Stack spacing={1}>
							{orderStatusMappings.map((row, index) => (
								<Stack key={`status-map-${index}`} direction="row" spacing={1} alignItems="center">
									<TextField
										size="small"
										label={t("LABELS.BACKEND_STATUS", { defaultValue: "Backend Status" })}
										value={row.backendStatus}
										onChange={(e) =>
											updateOrderStatusMapping(index, "backendStatus", e.target.value)
										}
										sx={{ flex: 1 }}
									/>
									<TextField
										size="small"
										label={t("LABELS.FRONTEND_STATUS_LABEL", { defaultValue: "Frontend Label" })}
										value={row.label}
										onChange={(e) => updateOrderStatusMapping(index, "label", e.target.value)}
										sx={{ flex: 1 }}
									/>
									<FormControlLabel
										sx={{ m: 0 }}
										control={
											<Switch
												checked={row.showInTracker !== false}
												onChange={(e) =>
													updateOrderStatusMapping(
														index,
														"showInTracker",
														e.target.checked
													)
												}
											/>
										}
										label={t("LABELS.SHOW_IN_TRACKER", { defaultValue: "Show" })}
									/>
									<IconButton
										aria-label="remove status mapping"
										onClick={() => removeOrderStatusMapping(index)}
									>
										<DeleteOutlineIcon fontSize="small" />
									</IconButton>
								</Stack>
							))}
						</Stack>
						<Button
							size="small"
							startIcon={<AddIcon />}
							sx={{ mt: 1 }}
							onClick={addOrderStatusMapping}
						>
							{t("BUTTON.ADD_STATUS_MAPPING", { defaultValue: "Add Status Mapping" })}
						</Button>
						<Box textAlign="right" sx={{ mt: 2 }}>
							<Button
								variant="contained"
								onClick={handleSaveFeatureSettings}
								startIcon={<Save />}
								disabled={featureSaving}
							>
								{featureSaving
									? t("BUTTON.SAVING", { defaultValue: "Saving..." })
									: t("BUTTON.SAVE_CHANGES", { defaultValue: "Save Settings" })}
							</Button>
						</Box>
					</Box>
				)}

				{isSuperAdmin && tab === publicRegistrationTabIndex && (
					<Box
						sx={{
							border: "1px solid",
							borderColor: "divider",
							borderRadius: 2,
							p: 2,
							mb: 3,
						}}
					>
						<Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
							{t("UI_TEXT.PUBLIC_REGISTRATION_SETTINGS", { defaultValue: "Public Registration Settings" })}
						</Typography>
						<Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
							{t("UI_TEXT.ALLOWED_REGISTRATION_EMAIL_DOMAINS_HELPER", {
								defaultValue:
									"Public registration rejects these email domains. Separate domains with comma, semicolon, space, or new line. An empty list allows all domains. Admin-created users are not restricted.",
							})}
						</Typography>
						{blockedDomainsError && (
							<Alert severity="error" sx={{ mb: 1 }}>
								{blockedDomainsError}
							</Alert>
						)}
						<Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 2 }}>
							<TextField
								label={t("LABELS.ALLOWED_REGISTRATION_EMAIL_DOMAINS", {
									defaultValue: "Blocked registration email domains",
								})}
								value={blockedDomainsInput}
								onChange={(e) => setBlockedDomainsInput(e.target.value)}
								size="small"
								fullWidth
								multiline
								minRows={1}
								placeholder="gmail.com, yahoo.com"
								disabled={Boolean(blockedDomainsError) || blockedDomainsSaving}
							/>
							<Button
								variant="contained"
								startIcon={<AddIcon />}
								onClick={handleAddBlockedDomains}
								disabled={
									Boolean(blockedDomainsError) ||
									blockedDomainsSaving ||
									!blockedDomainsInput.trim()
								}
							>
								{t("BUTTON.ADD", { defaultValue: "Add" })}
							</Button>
						</Stack>
						{blockedDomainsLoading ? (
							<Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
								<CircularProgress size={24} />
							</Box>
						) : (
							<TableContainer>
								<Table size="small">
									<TableHead>
										<TableRow>
											<TableCell>{t("LABELS.DOMAIN", { defaultValue: "Domain" })}</TableCell>
											<TableCell>{t("LABELS.ADDED_BY", { defaultValue: "Added by" })}</TableCell>
											<TableCell>{t("LABELS.ADDED_ON", { defaultValue: "Added on" })}</TableCell>
											<TableCell align="right" />
										</TableRow>
									</TableHead>
									<TableBody>
										{blockedDomains.length === 0 ? (
											<TableRow>
												<TableCell colSpan={4}>
													<Typography variant="body2" color="text.secondary">
														{t("UI_TEXT.NO_BLOCKED_DOMAINS", {
															defaultValue: "No blocked domains.",
														})}
													</Typography>
												</TableCell>
											</TableRow>
										) : (
											blockedDomains.map((row) => (
												<TableRow key={row.id}>
													<TableCell>{row.domain}</TableCell>
													<TableCell>{row.created_by_name || "-"}</TableCell>
													<TableCell>
														{row.created_at
															? new Date(row.created_at).toLocaleDateString()
															: "-"}
													</TableCell>
													<TableCell align="right">
														<IconButton
															aria-label="remove blocked domain"
															size="small"
															onClick={() => handleRemoveBlockedDomain(row)}
															disabled={Boolean(blockedDomainsError) || blockedDomainsSaving}
														>
															<DeleteOutlineIcon fontSize="small" />
														</IconButton>
													</TableCell>
												</TableRow>
											))
										)}
									</TableBody>
								</Table>
							</TableContainer>
						)}
					</Box>
				)}

				{isSuperAdmin && tab === footerTabIndex && (
					<Box
						sx={{
							border: "1px solid",
							borderColor: "divider",
							borderRadius: 2,
							p: 2,
							mb: 3,
						}}
					>
						<Typography variant="body1" sx={{ mb: 2 }} fontWeight={"bold"}>
							{t("LABELS.FOOTER_LINKS", { defaultValue: "Footer Links" })}
						</Typography>
						<FormGroup>
							<FormControlLabel
								control={
									<Switch
										checked={footerShowVersion}
										onChange={(e) => setFooterShowVersion(e.target.checked)}
									/>
								}
								label={t("LABELS.VERSION", { defaultValue: "LABELS.VERSION" })}
							/>
							<FormControlLabel
								control={
									<Switch
										checked={footerShowSupport}
										onChange={(e) => setFooterShowSupport(e.target.checked)}
									/>
								}
								label={t("LABELS.SUPPORT", { defaultValue: "Support" })}
							/>
							<FormControlLabel
								control={
									<Switch
										checked={footerShowTerms}
										onChange={(e) => setFooterShowTerms(e.target.checked)}
									/>
								}
								label={t("TITLES.TERMS_AND_CONDITIONS", { defaultValue: "Terms & Conditions" })}
							/>
							<FormControlLabel
								control={
									<Switch
										checked={footerShowEula}
										onChange={(e) => setFooterShowEula(e.target.checked)}
									/>
								}
								label={t("LABELS.EULA", { defaultValue: "EULA" })}
							/>
							<FormControlLabel
								control={
									<Switch
										checked={footerShowPrivacy}
										onChange={(e) => setFooterShowPrivacy(e.target.checked)}
									/>
								}
								label={t("LABELS.PRIVACY_POLICY", { defaultValue: "Privacy Policy" })}
							/>
							<FormControlLabel
								control={
									<Switch
										checked={footerShowCookiePolicy}
										onChange={(e) => setFooterShowCookiePolicy(e.target.checked)}
									/>
								}
								label={t("LABELS.COOKIE_POLICY", { defaultValue: "Cookie Policy" })}
							/>
							<FormControlLabel
								control={
									<Switch
										checked={footerShowManual}
										onChange={(e) => setFooterShowManual(e.target.checked)}
									/>
								}
								label={t("LABELS.MANUAL", { defaultValue: "Manual" })}
							/>
							<FormControlLabel
								control={
									<Switch
										checked={footerShowNeedHelp}
										onChange={(e) => setFooterShowNeedHelp(e.target.checked)}
									/>
								}
								label={t("LABELS.NEED_HELP", { defaultValue: "Need Help?" })}
							/>
						</FormGroup>
						<Box textAlign="right" sx={{ mt: 2 }}>
							<Button
								variant="contained"
								onClick={handleSaveFooterLinks}
								startIcon={<Save />}
								disabled={footerSaving}
							>
								{footerSaving
									? t("BUTTON.SAVING", { defaultValue: "Saving..." })
									: t("BUTTON.SAVE_CHANGES", { defaultValue: "Save Settings" })}
							</Button>
						</Box>
					</Box>
				)}

				{isSuperAdmin && tab === siteSelectionTabIndex && (
					<Box
						sx={{
							border: "1px solid",
							borderColor: "divider",
							borderRadius: 2,
							p: 2,
							mb: 3,
						}}
					>
						<Typography variant="body1" sx={{ mb: 2 }} fontWeight={"bold"}>
							{t("LABELS.SITE_SELECTION_SETTINGS", { defaultValue: "Site Selection Settings" })}
						</Typography>
						<FormControlLabel
							control={
								<Switch
									checked={siteGroupingEnabled}
									onChange={(e) => setSiteGroupingEnabled(e.target.checked)}
								/>
							}
							label={t("LABELS.ENABLE_CUSTOMER_GROUPING_IN_SITE_SELECTION", {
								defaultValue: "Enable customer grouping in site selection",
							})}
						/>
						<FormControlLabel
							control={
								<Switch
									checked={siteNumberVisible}
									onChange={(e) => setSiteNumberVisible(e.target.checked)}
								/>
							}
							label={t("LABELS.SHOW_CUSTOMER_SITE_NUMBER_IN_SITE_SELECTION", {
								defaultValue: "Show customer site number in site selection",
							})}
						/>
						<Box textAlign="right" sx={{ mt: 2 }}>
							<Button
								variant="contained"
								onClick={handleSaveSiteSelection}
								startIcon={<Save />}
								disabled={siteSaving}
							>
								{siteSaving
									? t("BUTTON.SAVING", { defaultValue: "Saving..." })
									: t("BUTTON.SAVE_CHANGES", { defaultValue: "Save Settings" })}
							</Button>
						</Box>
					</Box>
				)}

				{isSuperAdmin && tab === rsplTabIndex && (
					<Box
						sx={{
							border: "1px solid",
							borderColor: "divider",
							borderRadius: 2,
							p: 2,
							mb: 3,
						}}
					>
						<Typography variant="body1" sx={{ mb: 2 }} fontWeight={"bold"}>
							{t("LABELS.RSPL_SETTINGS", { defaultValue: "RSPL Settings" })}
						</Typography>
						<Stack spacing={2}>
							{rsplRows.map((row, index) => (
								<Box key={`${row.key}-${index}`} sx={{ display: "flex", gap: 2, alignItems: "center" }}>
									<TextField
										label={t("LABELS.KEY", { defaultValue: "Key" })}
										value={row.key}
										onChange={(e) => updateRsplRow(index, "key", e.target.value)}
										size="small"
										fullWidth
									/>
									<TextField
										label={t("LABELS.VALUE", { defaultValue: "Value" })}
										value={row.value}
										onChange={(e) => updateRsplRow(index, "value", e.target.value)}
										size="small"
										fullWidth
									/>
									<IconButton onClick={() => removeRsplRow(index)} aria-label="Remove">
										<DeleteOutlineIcon />
									</IconButton>
								</Box>
							))}
							<Box>
								<Button variant="outlined" startIcon={<AddIcon />} onClick={addRsplRow}>
									{t("TABLE_HEADERS.ADD", { defaultValue: "Add" })}
								</Button>
							</Box>
						</Stack>
						{rsplError && (
							<Typography variant="body2" color="error" sx={{ mt: 1 }}>
								{rsplError}
							</Typography>
						)}
						<Box textAlign="right" sx={{ mt: 2 }}>
							<Button
								variant="contained"
								onClick={handleSaveRsplSettings}
								startIcon={<Save />}
								disabled={rsplSaving}
							>
								{rsplSaving
									? t("BUTTON.SAVING", { defaultValue: "Saving..." })
									: t("BUTTON.SAVE_CHANGES", { defaultValue: "Save RSPL Settings" })}
							</Button>
						</Box>
					</Box>
				)}

				{isSuperAdmin && tab === model3dTabIndex && (
					<Box
						sx={{
							border: "1px solid",
							borderColor: "divider",
							borderRadius: 2,
							p: 2,
							mb: 3,
						}}
					>
						<Typography variant="body1" sx={{ mb: 2 }} fontWeight={"bold"}>
							{t("LABELS.MODEL_3D_SETTINGS", { defaultValue: "3D Model Settings" })}
						</Typography>
						<Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
							{t("LABELS.DEFAULT_MODEL_ORIENTATION", { defaultValue: "Default Model Orientation" })}
						</Typography>
						<Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" }, gap: 2, mb: 2 }}>
							<FormControl fullWidth size="small">
								<InputLabel>{t("LABELS.UP_AXIS", { defaultValue: "Up Axis" })}</InputLabel>
								<Select
									value={modelOrientationUp}
									label={t("LABELS.UP_AXIS", { defaultValue: "Up Axis" })}
									onChange={(e) => setModelOrientationUp(e.target.value)}
								>
									{MODEL_AXIS_CHOICES.map((axis) => (
										<MenuItem key={`up-${axis}`} value={axis}>
											{axis}
										</MenuItem>
									))}
								</Select>
							</FormControl>
							<FormControl fullWidth size="small">
								<InputLabel>{t("LABELS.LEFT_AXIS", { defaultValue: "Left Axis" })}</InputLabel>
								<Select
									value={modelOrientationLeft}
									label={t("LABELS.LEFT_AXIS", { defaultValue: "Left Axis" })}
									onChange={(e) => setModelOrientationLeft(e.target.value)}
								>
									{MODEL_AXIS_CHOICES.map((axis) => (
										<MenuItem key={`left-${axis}`} value={axis}>
											{axis}
										</MenuItem>
									))}
								</Select>
							</FormControl>
							<FormControl fullWidth size="small">
								<InputLabel>{t("LABELS.RIGHT_AXIS", { defaultValue: "Right Axis" })}</InputLabel>
								<Select
									value={modelOrientationRight}
									label={t("LABELS.RIGHT_AXIS", { defaultValue: "Right Axis" })}
									onChange={(e) => setModelOrientationRight(e.target.value)}
								>
									{MODEL_AXIS_CHOICES.map((axis) => (
										<MenuItem key={`right-${axis}`} value={axis}>
											{axis}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						</Box>
						{new Set([modelOrientationUp, modelOrientationLeft, modelOrientationRight]).size !== 3 && (
							<Typography variant="body2" color="error" sx={{ mb: 2 }}>
								{t("UI_TEXT.MODEL_ORIENTATION_AXES_MUST_BE_UNIQUE", {
									defaultValue: "Choose unique axes for Up, Left, and Right.",
								})}
							</Typography>
						)}
						<FormControl fullWidth size="small" sx={{ mb: 2 }}>
							<InputLabel>{t("LABELS.DEFAULT_CAMERA_CORNER", { defaultValue: "Default Camera Corner" })}</InputLabel>
							<Select
								value={modelDefaultCameraCorner}
								label={t("LABELS.DEFAULT_CAMERA_CORNER", { defaultValue: "Default Camera Corner" })}
								onChange={(e) => setModelDefaultCameraCorner(e.target.value)}
							>
								{MODEL_CAMERA_CORNER_CHOICES.map((corner) => (
									<MenuItem key={corner} value={corner}>
										{t(`LABELS.CAMERA_CORNER_${corner.replace("-", "_").toUpperCase()}`, {
											defaultValue: corner
												.split("-")
												.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
												.join(" "),
										})}
									</MenuItem>
								))}
							</Select>
						</FormControl>
						<Box sx={{ mt: 2 }}>
							<Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
								{t("LABELS.LIGHT_INTENSITY", { defaultValue: "Light Intensity" })}
							</Typography>
							<Slider
								value={modelLightIntensity}
								min={0.2}
								max={2}
								step={0.1}
								onChange={(_, value) =>
									setModelLightIntensity(Array.isArray(value) ? value[0] : value)
								}
								valueLabelDisplay="auto"
								sx={{ maxWidth: 320 }}
							/>
						</Box>
						<Box sx={{ mt: 2 }}>
							<Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
								{t("LABELS.HIGHLIGHT_COLOR", { defaultValue: "Highlight Color" })}
							</Typography>
							<Box sx={{ display: "flex", justifyContent: "flex-start", alignItems: "center", width: "100%", columnGap: 2 }}>
								<Box sx={{ display: "flex", justifyContent: "flex-start", alignItems: "flex-start", flexDirection: "column", width: "100%", rowGap: 2 }}>
									<Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
										{presetColors.map(({ key, palette }) => (
											<Button
												key={key}
												variant={modelHighlightColor === key ? "contained" : "outlined"}
												onClick={() => setModelHighlightColor(key)}
												sx={{
													minWidth: 42,
													width: 42,
													height: 42,
													borderRadius: "50%",
													borderColor: palette.main,
													backgroundColor: palette.main,
													color: "#fff",
													opacity: modelHighlightColor === key ? 1 : 0.7,
												}}
											>
												{" "}
											</Button>
										))}
									</Stack>
									<Divider flexItem>{t('LABELS.OR')}</Divider>
									<TextField
										label={t("LABELS.CUSTOM", { defaultValue: "Custom" })}
										placeholder="#0066FF"
										value={isValidHex(modelHighlightColor) ? modelHighlightColor : ""}
										onChange={(e) => setModelHighlightColor(e.target.value)}
										size="small"
										fullWidth
										helperText={t("LABELS.ENTER_A_HEX_VALUE_TO_SET_A_CUSTOM_ACCENT_COLOR", {
											defaultValue: "Enter a hex value to set a custom highlight color",
										})}
									/>
								</Box>
								{(() => {
									const colorPreview = themeColors[modelHighlightColor]?.main
										|| (isValidHex(modelHighlightColor) ? modelHighlightColor : null);
									return colorPreview ? (
										<>
											<Divider orientation="vertical" flexItem />
											<Box
												sx={{
													minWidth: "120px",
													height: "100%",
													minHeight: "120px",
													width: "120px",
													borderRadius: "15px",
													border: "1px solid",
													borderColor: "divider",
													backgroundColor: colorPreview,
													display: "flex",
													justifyContent: "center",
													alignItems: "center",
												}}
											>
												<Typography variant="body1" color="primary.contrastText">{t("LABELS.SITE_SELECTION_SETTINGS")}</Typography>
											</Box>
										</>
									) : null;
								})()}
							</Box>
						</Box>
						<Box sx={{ mt: 2 }}>
							<Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
								{t("UI_TEXT.HIGHLIGHT_OPACITY", { defaultValue: "Highlight Opacity" })}
							</Typography>
							<Slider
								value={modelHighlightOpacity}
								min={0.1}
								max={1}
								step={0.05}
								onChange={(_, value) =>
									setModelHighlightOpacity(Array.isArray(value) ? value[0] : value)
								}
								valueLabelDisplay="auto"
								sx={{ maxWidth: 320 }}
							/>
						</Box>
						<Box sx={{ mt: 2 }}>
							<Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
								{t("LABELS.MENU_TAB_TITLES", { defaultValue: "Menu Tab Titles" })}
							</Typography>
							<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
								<TextField
									label={t("LABELS.SUB_ASSEMBLIES_TAB", { defaultValue: "Sub-Assemblies Tab" })}
									value={modelMenuTabSubassemblies}
									onChange={(e) => setModelMenuTabSubassemblies(e.target.value)}
									size="small"
									fullWidth
								/>
								<TextField
									label={t("LABELS.RSPL_PARTS_TAB", { defaultValue: "RSPL Parts Tab" })}
									value={modelMenuTabRspl}
									onChange={(e) => setModelMenuTabRspl(e.target.value)}
									size="small"
									fullWidth
								/>
								<TextField
									label={t("LABELS.PARTS_TREE_TAB", { defaultValue: "Parts Tree Tab" })}
									value={modelMenuTabPartsTree}
									onChange={(e) => setModelMenuTabPartsTree(e.target.value)}
									size="small"
									fullWidth
								/>
								<FormControlLabel
									control={
										<Switch
											checked={modelMenuShowSubtitles}
											onChange={(e) => setModelMenuShowSubtitles(e.target.checked)}
										/>
									}
									label={t("LABELS.SHOW_TAB_SUBTITLES", { defaultValue: "Show tab subtitles" })}
								/>
							</Box>
						</Box>
						<Box textAlign="right" sx={{ mt: 2 }}>
							<Button
								variant="contained"
								onClick={handleSaveModelSettings}
								startIcon={<Save />}
								disabled={modelAxisSaving}
							>
								{modelAxisSaving
									? t("BUTTON.SAVING", { defaultValue: "Saving..." })
									: t("BUTTON.SAVE_CHANGES", { defaultValue: "Save Settings" })}
							</Button>
						</Box>
					</Box>
				)}

				{isSuperAdmin && tab === customerLocationTabIndex && (
					<Box
						sx={{
							border: "1px solid",
							borderColor: "divider",
							borderRadius: 2,
							p: 2,
							mb: 3,
						}}
					>
						<Typography variant="body1" sx={{ mb: 2 }} fontWeight={"bold"}>
							{t("LABELS.CUSTOMER_LOCATION_SETTINGS", { defaultValue: "Customer Location Settings" })}
						</Typography>
						<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
							<Box>
								<Typography variant="subtitle1" fontWeight={600}>
									{t("UI_TEXT.SERVICES", { defaultValue: "Services" })}
								</Typography>
								<Typography variant="body2" color="text.secondary">
									{t("LABELS.ENABLE_THE_SERVICES_TAB_AND_MANAGE_AVAILABLE_SERVICES", { defaultValue: "Enable the Services tab and manage available services." })}
								</Typography>
							</Box>
							<FormControlLabel
								control={
									<Switch
										checked={customerLocationServicesEnabled}
										onChange={(e) => setCustomerLocationServicesEnabled(e.target.checked)}
									/>
								}
								label={t("LABELS.ENABLE_SERVICES_TAB", { defaultValue: "Enable Services Tab" })}
							/>
							{customerLocationServicesEnabled && (
								<>
									<Stack spacing={1.5}>
										{customerLocationServiceRows.map((row, index) => (
											<Box key={`${row.key}-${index}`} sx={{ display: "flex", gap: 1, alignItems: "center" }}>
												<TextField
													label={t("UI_TEXT.SERVICE_KEY", { defaultValue: "Key" })}
													value={row.key}
													onChange={(e) => updateCustomerLocationServiceRow(index, "key", e.target.value)}
													size="small"
													fullWidth
												/>
												<TextField
													label={t("LABELS.LABEL", { defaultValue: "Label" })}
													value={row.label}
													onChange={(e) => updateCustomerLocationServiceRow(index, "label", e.target.value)}
													size="small"
													fullWidth
												/>
												<TextField
													label={t("LABELS.DESCRIPTION", { defaultValue: "Description" })}
													value={row.description}
													onChange={(e) => updateCustomerLocationServiceRow(index, "description", e.target.value)}
													size="small"
													fullWidth
												/>
												<TextField
													label={t("UI_TEXT.SERVICE_URL", { defaultValue: "URL" })}
													value={row.href}
													onChange={(e) => updateCustomerLocationServiceRow(index, "href", e.target.value)}
													size="small"
													fullWidth
												/>
												<IconButton onClick={() => removeCustomerLocationServiceRow(index)} aria-label="Remove">
													<DeleteOutlineIcon />
												</IconButton>
											</Box>
										))}
									</Stack>
									<Box>
										<Button variant="outlined" startIcon={<AddIcon />} onClick={addCustomerLocationServiceRow}>
											{t("TABLE_HEADERS.ADD", { defaultValue: "Add" })}
										</Button>
									</Box>
									{customerLocationServiceError && (
										<Typography variant="body2" color="error">
											{customerLocationServiceError}
										</Typography>
									)}
								</>
							)}
						</Box>
						<Divider sx={{ my: 2 }} />
						<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
							<Typography variant="subtitle1" fontWeight={600}>
								{t("LABELS.LOCATION_OPTIONS", { defaultValue: "Location Options" })}
							</Typography>
							<FormGroup>
								<FormControlLabel
									control={
										<Switch
											checked={customerLocationShowLegalEntity}
											onChange={(e) => setCustomerLocationShowLegalEntity(e.target.checked)}
										/>
									}
									label={t("LABELS.SHOW_DEFAULT_LEGAL_ENTITY", { defaultValue: "Show Default Legal Entity" })}
								/>
								<FormControlLabel
									control={
										<Switch
											checked={customerLocationShowSite}
											onChange={(e) => setCustomerLocationShowSite(e.target.checked)}
										/>
									}
									label={t("LABELS.SHOW_SITE", { defaultValue: "Show Site" })}
								/>
								<FormControlLabel
									control={
										<Switch
											checked={customerLocationShowWarehouse}
											onChange={(e) => setCustomerLocationShowWarehouse(e.target.checked)}
										/>
									}
									label={t("LABELS.SHOW_WAREHOUSE", { defaultValue: "Show Warehouse" })}
								/>
								<FormControlLabel
									control={
										<Switch
											checked={customerLocationShowPaymentTerms}
											onChange={(e) => setCustomerLocationShowPaymentTerms(e.target.checked)}
										/>
									}
									label={t("LABELS.SHOW_PAYMENT_TERMS", { defaultValue: "Show Payment Terms" })}
								/>
								<FormControlLabel
									control={
										<Switch
											checked={customerLocationShowOnHold}
											onChange={(e) => setCustomerLocationShowOnHold(e.target.checked)}
										/>
									}
									label={t("LABELS.SHOW_ON_HOLD", { defaultValue: "Show On Hold Status" })}
								/>
							</FormGroup>
						</Box>
						<Divider sx={{ my: 2 }} />
						<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
							<Typography variant="subtitle1" fontWeight={600}>
								{t("LABELS.EMERGENCY_CONTACTS_BY_REGION", { defaultValue: "Emergency Contacts by Region" })}
							</Typography>
							<Typography variant="body2" color="text.secondary">
								{t("LABELS.CONFIGURE_THE_EMERGENCY_CONTACT_NUMBER_USED_IN_THE_CONTACT_CCR_FORM_FOR_EACH_REGION", { defaultValue: "Configure the emergency contact number used in the Contact CCR form for each region." })}
							</Typography>
							<Stack spacing={1.5} sx={{ mt: 1 }}>
								{REGION_PHONE_DEFAULTS.map((row) => {
									const current = (customerLocationEmergencyPhones || []).find((r) => r.region === row.region) || row;
									const selectedIso2 = String(current.iso2 || REGION_DEFAULT_ISO2[row.region] || "US").toUpperCase();
									const selectedCountry = phoneCountries.find((country) => country.code === selectedIso2);
									return (
										<Box
											key={row.region}
											sx={{
												display: "grid",
												gridTemplateColumns: { xs: "1fr", md: "140px 320px 320px" },
												alignItems: "start",
												columnGap: 2,
												rowGap: 1,
											}}
										>
											<Typography
												sx={{
													minWidth: 140,
													fontWeight: 600,
													pt: { xs: 0, md: "8px" },
												}}
											>
												{row.region}
											</Typography>
											<Autocomplete
												options={phoneCountryOptions}
												filterOptions={phoneCountryFilter}
												autoHighlight
												selectOnFocus
												handleHomeEndKeys
												openOnFocus
												clearOnBlur={false}
												value={
													phoneCountryOptions.find((country) => country.iso2 === selectedIso2) || null
												}
												onChange={(_, nextValue) => {
													updateEmergencyPhoneCountry(row.region, nextValue?.iso2 || selectedIso2);
												}}
												isOptionEqualToValue={(option, value) => option.iso2 === value.iso2}
												getOptionLabel={(option) =>
													option?.label
														? `+${option.dialCode || ""} ${option.label}`
														: ""
												}
												sx={{ width: "100%", maxWidth: 320 }}
												renderOption={(props, option, state) => (
													<Box component="li" {...props} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
														<img
															loading="lazy"
															width="20"
															src={`https://flagcdn.com/w40/${option.iso2.toLowerCase()}.png`}
															alt=""
														/>
														<Typography variant="caption" color="text.secondary" sx={{ minWidth: 52 }}>
															+
															{splitHighlightedText(option.dialCode, state.inputValue).map((part, index) => (
																<Box
																	component="span"
																	key={`${part.text}-${index}`}
																	sx={{ fontWeight: part.highlighted ? 700 : 400 }}
																>
																	{part.text}
																</Box>
															))}
														</Typography>
														<Box sx={{ minWidth: 0, flex: 1 }}>
															<Typography variant="body2" noWrap>
																{splitHighlightedText(option.label, state.inputValue).map((part, index) => (
																	<Box
																		component="span"
																		key={`${part.text}-${index}`}
																		sx={{ fontWeight: part.highlighted ? 700 : 400 }}
																	>
																		{part.text}
																	</Box>
																))}
															</Typography>
														</Box>
														<Typography variant="caption" color="text.secondary">
															{splitHighlightedText(option.iso2, state.inputValue).map((part, index) => (
																<Box
																	component="span"
																	key={`${part.text}-${index}`}
																	sx={{ fontWeight: part.highlighted ? 700 : 400 }}
																>
																	{part.text}
																</Box>
															))}
														</Typography>
													</Box>
												)}
												renderInput={(params) => (
													<TextField
														{...params}
														label={t("LABELS.COUNTRY", { defaultValue: "Country" })}
														placeholder={t("LABELS.SEARCH_COUNTRY_CODE", { defaultValue: "Search country, ISO, or code" })}
														size="small"
														helperText={t("LABELS.SEARCHABLE_COUNTRY_SELECTOR", { defaultValue: "Search by country name, ISO code, or dialing code." })}
														InputProps={{
															...params.InputProps,
															startAdornment: selectedIso2 ? (
																<>
																	<InputAdornment position="start" sx={{ mr: 1 }}>
																		<img
																			loading="lazy"
																			width="20"
																			src={`https://flagcdn.com/w40/${selectedIso2.toLowerCase()}.png`}
																			alt=""
																		/>
																	</InputAdornment>
																	{params.InputProps.startAdornment}
																</>
															) : params.InputProps.startAdornment,
														}}
													/>
												)}
											/>
											<Box sx={{ width: "100%", maxWidth: 320 }}>
												<TextField
													size="small"
													fullWidth
													label={t("LABELS.EMERGENCY_CONTACT", { defaultValue: "Emergency Contact" })}
													value={current.phone || ""}
													onChange={(e) => updateEmergencyPhone(row.region, e.target.value)}
													inputProps={{ maxLength: 18 }}
													placeholder={selectedCountry ? `+${sanitizeDialCode(selectedCountry.phone)} 1234567890` : SUPPORT_PHONE_PLACEHOLDER}
													helperText={t("LABELS.SELECT_COUNTRY_AND_ENTER_NUMBER", { defaultValue: "Select a country/flag and enter the emergency number for this region." })}
												/>
											</Box>
										</Box>
									);
								})}
							</Stack>
						</Box>
						<Box textAlign="right" sx={{ mt: 2 }}>
							<Button
								variant="contained"
								onClick={handleSaveCustomerLocationSettings}
								startIcon={<Save />}
								disabled={customerLocationSaving}
							>
								{customerLocationSaving
									? t("BUTTON.SAVING", { defaultValue: "Saving..." })
									: t("BUTTON.SAVE_CHANGES", { defaultValue: "Save Settings" })}
							</Button>
						</Box>
					</Box>
				)}

				{isSuperAdmin && tab === supportInfoTabIndex && (
					<Box
						sx={{
							border: "1px solid",
							borderColor: "divider",
							borderRadius: 2,
							p: 2,
							mb: 3,
						}}
					>
						<Typography variant="body1" sx={{ mb: 2 }} fontWeight={"bold"}>
							{t("UI_TEXT.SUPPORT_INFORMATION", { defaultValue: "Support Information" })}
						</Typography>
						<Stack spacing={2}>
							<Box>
								<Typography variant="subtitle1" fontWeight={600}>Contact CCR Form</Typography>
								<FormGroup>
									<FormControlLabel
										control={<Switch checked={supportContactCcrShowEmergencyMessage} onChange={(e) => setSupportContactCcrShowEmergencyMessage(e.target.checked)} />}
										label="Show emergency contact message"
									/>
								</FormGroup>
								<FormControl size="small" fullWidth sx={{ mt: 1, maxWidth: 360 }}>
									<InputLabel>Emergency Number Source</InputLabel>
									<Select
										label="Emergency Number Source"
										value={supportContactCcrEmergencyNumberSource}
										onChange={(e) => setSupportContactCcrEmergencyNumberSource(e.target.value)}
									>
										<MenuItem value="region">Configured emergency number by user region</MenuItem>
										<MenuItem value="manual">Use manual emergency number</MenuItem>
									</Select>
								</FormControl>
								{supportContactCcrEmergencyNumberSource === "manual" && (
									<TextField
										size="small"
										fullWidth
										sx={{ mt: 1, maxWidth: 360 }}
										label="Emergency Number"
										placeholder={SUPPORT_PHONE_PLACEHOLDER}
										value={supportContactCcrEmergencyNumberManual}
										onChange={(e) => setSupportContactCcrEmergencyNumberManual(e.target.value)}
										error={supportContactCcrEmergencyNumberManual !== "" && !isValidEmergencyPhone(supportContactCcrEmergencyNumberManual)}
										helperText="Format: +countrycode 10digits"
									/>
								)}
							</Box>
							<Divider />
							<Box>
								<Typography variant="subtitle1" fontWeight={600}>Create Case Form</Typography>
								<FormGroup>
									<FormControlLabel
										control={<Switch checked={supportCreateCaseShowEmergencyMessage} onChange={(e) => setSupportCreateCaseShowEmergencyMessage(e.target.checked)} />}
										label="Show emergency contact message"
									/>
								</FormGroup>
								<FormControl size="small" fullWidth sx={{ mt: 1, maxWidth: 360 }}>
									<InputLabel>Emergency Number Source</InputLabel>
									<Select
										label="Emergency Number Source"
										value={supportCreateCaseEmergencyNumberSource}
										onChange={(e) => setSupportCreateCaseEmergencyNumberSource(e.target.value)}
									>
										<MenuItem value="region">Configured emergency number by user region</MenuItem>
										<MenuItem value="manual">Use manual emergency number</MenuItem>
									</Select>
								</FormControl>
								{supportCreateCaseEmergencyNumberSource === "manual" && (
									<TextField
										size="small"
										fullWidth
										sx={{ mt: 1, maxWidth: 360 }}
										label="Emergency Number"
										placeholder={SUPPORT_PHONE_PLACEHOLDER}
										value={supportCreateCaseEmergencyNumberManual}
										onChange={(e) => setSupportCreateCaseEmergencyNumberManual(e.target.value)}
										error={supportCreateCaseEmergencyNumberManual !== "" && !isValidEmergencyPhone(supportCreateCaseEmergencyNumberManual)}
										helperText="Format: +countrycode 10digits"
									/>
								)}
								<Box sx={{ mt: 1 }}>
									<Typography variant="body2" color={(optionKeys?.supportErpConfigured || "false") === "true" ? "success.main" : "warning.main"}>
										ERP configuration status: {(optionKeys?.supportErpConfigured || "false") === "true" ? "Configured" : "Not Configured"}
									</Typography>
								</Box>
								<FormGroup>
									<FormControlLabel
										control={
											<Switch
												checked={supportEnableErpCaseCreation}
												onChange={(e) => setSupportEnableErpCaseCreation(e.target.checked)}
												disabled={(optionKeys?.supportErpConfigured || "false") !== "true"}
											/>
										}
										label="Enable case creation in ERP"
									/>
								</FormGroup>
							</Box>
							<Divider />
							<Box>
								<Typography variant="subtitle1" fontWeight={600}>Case Type Options</Typography>
								<Stack spacing={1.5} sx={{ mt: 1 }}>
									{(supportCaseTypeConfig.options || []).map((option, index) => (
										<Box key={`case-type-${index}`} sx={{ display: "flex", gap: 1, alignItems: "center" }}>
											<TextField label="Value" value={option.value} size="small" onChange={(e) => updateSupportCaseTypeOption(index, "value", e.target.value)} sx={{ width: 220 }} />
											<TextField label="Label" value={option.label} size="small" onChange={(e) => updateSupportCaseTypeOption(index, "label", e.target.value)} sx={{ flex: 1 }} />
											<IconButton onClick={() => removeSupportCaseTypeOption(index)}><DeleteOutlineIcon /></IconButton>
										</Box>
									))}
								</Stack>
								<Button sx={{ mt: 1 }} variant="outlined" startIcon={<AddIcon />} onClick={addSupportCaseTypeOption}>Add Case Type Option</Button>
							</Box>
							<Divider />
							<Box>
								<Typography variant="subtitle1" fontWeight={600}>Create Case Field Visibility</Typography>
								<FormGroup>
									<FormControlLabel
										control={<Switch checked={supportCreateCaseFieldVisibility.requester_name !== false} onChange={(e) => setSupportCreateCaseFieldVisibility((prev) => ({ ...prev, requester_name: e.target.checked }))} />}
										label={t("UI_TEXT.REQUESTER_NAME", { defaultValue: "Requester Name" })}
									/>
									<FormControlLabel
										control={<Switch checked={supportCreateCaseFieldVisibility.requester_email !== false} onChange={(e) => setSupportCreateCaseFieldVisibility((prev) => ({ ...prev, requester_email: e.target.checked }))} />}
										label={t("UI_TEXT.REQUESTER_EMAIL", { defaultValue: "Requester Email" })}
									/>
									<FormControlLabel
										control={<Switch checked={supportCreateCaseFieldVisibility.requester_phone !== false} onChange={(e) => setSupportCreateCaseFieldVisibility((prev) => ({ ...prev, requester_phone: e.target.checked }))} />}
										label={t("LABELS.PHONE", { defaultValue: "Phone" })}
									/>
									<FormControlLabel
										control={<Switch checked={supportCreateCaseFieldVisibility.machine_number !== false} onChange={(e) => setSupportCreateCaseFieldVisibility((prev) => ({ ...prev, machine_number: e.target.checked }))} />}
										label={t("TABLE_HEADERS.MACHINE_NUMBER", { defaultValue: "Machine Number" })}
									/>
								</FormGroup>
								<Divider sx={{ my: 1.5 }} />
								<Typography variant="subtitle1" fontWeight={600}>Case Safety</Typography>
								<FormControlLabel
									control={<Switch checked={supportCaseSafetyConfig.visible !== false} onChange={(e) => setSupportCaseSafetyConfig((prev) => ({ ...prev, visible: e.target.checked }))} />}
									label="Show Case Safety Field"
								/>
								<Typography variant="subtitle1" fontWeight={600}>Case Safety Options</Typography>
								<Stack spacing={1.5} sx={{ mt: 1 }}>
									{(supportCaseSafetyConfig.options || []).map((option, index) => (
										<Box key={`case-safety-${index}`} sx={{ display: "flex", gap: 1, alignItems: "center" }}>
											<TextField label="Value" value={option.value} size="small" onChange={(e) => updateSupportCaseSafetyOption(index, "value", e.target.value)} sx={{ width: 220 }} />
											<TextField label="Label" value={option.label} size="small" onChange={(e) => updateSupportCaseSafetyOption(index, "label", e.target.value)} sx={{ flex: 1 }} />
											<IconButton onClick={() => removeSupportCaseSafetyOption(index)}><DeleteOutlineIcon /></IconButton>
										</Box>
									))}
								</Stack>
								<Button sx={{ mt: 1 }} variant="outlined" startIcon={<AddIcon />} onClick={addSupportCaseSafetyOption}>Add Case Safety Option</Button>
							</Box>
						</Stack>
						<Box textAlign="right" sx={{ mt: 2 }}>
							<Button variant="contained" onClick={handleSaveSupportInformation} startIcon={<Save />} disabled={supportInfoSaving}>
								{supportInfoSaving ? t("BUTTON.SAVING", { defaultValue: "Saving..." }) : t("BUTTON.SAVE_CHANGES", { defaultValue: "Save Settings" })}
							</Button>
						</Box>
					</Box>
				)}

				{isSuperAdmin && tab === adminModulesTabIndex && (
					<Box
						sx={{
							border: "1px solid",
							borderColor: "divider",
							borderRadius: 2,
							p: 2,
							mb: 3,
						}}
					>
						<Typography variant="body1" sx={{ mb: 2 }} fontWeight={"bold"}>
							{t("LABELS.ADMIN_MODULE_AVAILABILITY", { defaultValue: "Admin Module Availability" })}
						</Typography>
						<Box
							sx={{
								display: "grid",
								gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
								columnGap: 3,
								rowGap: 2,
								px: { xs: 0.5, md: 1 },
							}}
						>
							{groupedAdminModules.map(({ group, modules }) => (
								<Box
									key={group}
									sx={{
										border: "1px solid",
										borderColor: "divider",
										borderRadius: 1,
										px: 2,
										py: 1.5,
									}}
								>
									<Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
										{group}
									</Typography>
									<FormGroup sx={{ rowGap: 0.5 }}>
										{modules.map((module) => (
											<FormControlLabel
												key={module.key}
												sx={{
													mx: 0,
													justifyContent: "space-between",
													".MuiFormControlLabel-label": { fontSize: 14 },
												}}
												control={
													<Switch
														checked={adminEnabledModules.includes(module.key)}
														onChange={() => toggleAdminModule(module.key)}
													/>
												}
												label={t(module.labelKey || module.key, { defaultValue: module.labelKey || module.key })}
												labelPlacement="start"
											/>
										))}
									</FormGroup>
								</Box>
							))}
						</Box>
						<Box textAlign="right" sx={{ mt: 2 }}>
							<Button
								variant="contained"
								onClick={handleSaveAdminModules}
								startIcon={<Save />}
								disabled={adminModulesSaving}
							>
								{adminModulesSaving
									? t("BUTTON.SAVING", { defaultValue: "Saving..." })
									: t("BUTTON.SAVE_CHANGES", { defaultValue: "Save Settings" })}
							</Button>
						</Box>
					</Box>
				)}
				{isSuperAdmin && tab === cookieTabIndex && (
					<Box
						sx={{
							border: "1px solid",
							borderColor: "divider",
							borderRadius: 2,
							p: 2,
							mb: 3,
						}}
					>
						<Typography variant="body1" sx={{ mb: 2 }} fontWeight={"bold"}>
							{t("UI_TEXT.COOKIE_SETTINGS", { defaultValue: "Cookie Settings" })}
						</Typography>
						<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
							{t("UI_TEXT.COOKIE_SETTINGS_HELP", {
								defaultValue: "Number of days after which users must accept the cookie policy again.",
							})}
						</Typography>
						<TextField
							label={t("UI_TEXT.COOKIE_REPROMPT_DAYS", { defaultValue: "Re-prompt Days" })}
							type="number"
							inputProps={{ min: 1 }}
							value={cookieConsentRePromptDays}
							onChange={(e) => setCookieConsentRePromptDays(e.target.value)}
							size="small"
							fullWidth
						/>
						<Box textAlign="right" sx={{ mt: 2 }}>
							<Button
								variant="contained"
								onClick={handleSaveCookieSettings}
								startIcon={<Save />}
								disabled={cookieSettingsSaving}
							>
								{cookieSettingsSaving
									? t("BUTTON.SAVING", { defaultValue: "Saving..." })
									: t("BUTTON.SAVE_CHANGES", { defaultValue: "Save Settings" })}
							</Button>
						</Box>
					</Box>
				)}

				{isSuperAdmin && tab === stepSyncReportTabIndex && (
					<Box
						sx={{
							border: "1px solid",
							borderColor: "divider",
							borderRadius: 2,
							p: 2,
							mb: 3,
						}}
					>
						<Typography variant="body1" sx={{ mb: 1 }} fontWeight={"bold"}>
							{t("LABELS.STEP_SYNC_REPORTS", { defaultValue: "STEP File Sync Reports" })}
						</Typography>
						<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
							{t("UI_TEXT.STEP_SYNC_REPORTS_HELP", {
								defaultValue:
									"Once a day, at the time set below, email a single report of the STEP files that finished processing and were published to their final location, listing the customer LN number and machine number of each. Onboarded and failed models appear as separate tables in the same email. Only newly onboarded models are reported — a night where nothing new landed sends nothing.",
							})}
						</Typography>

						<FormControlLabel
							control={
								<Switch
									checked={stepSyncReportEnabled}
									onChange={(e) => setStepSyncReportEnabled(e.target.checked)}
								/>
							}
							label={t("UI_TEXT.STEP_SYNC_REPORTS_ENABLE", {
								defaultValue: "Send STEP file sync reports",
							})}
							sx={{ mb: 2, display: "block" }}
						/>

						<TextField
							type="time"
							label={t("UI_TEXT.STEP_SYNC_REPORT_SEND_TIME", {
								defaultValue: "Nightly Send Time",
							})}
							value={stepSyncReportSendTime}
							onChange={(e) => setStepSyncReportSendTime(e.target.value || "05:00")}
							helperText={t("UI_TEXT.STEP_SYNC_REPORT_SEND_TIME_HELP", {
								defaultValue:
									"The report is sent once per day at this time, US Eastern (America/New_York, so it stays correct across daylight saving), and covers only what finished in the 24 hours since the previous report. Machines that finish later are held for the next night's report.",
							})}
							size="small"
							disabled={!stepSyncReportEnabled}
							InputLabelProps={{ shrink: true }}
							inputProps={{ step: 300 }}
							sx={{ mb: 2, width: 220 }}
						/>

						<Autocomplete
							multiple
							size="small"
							options={roleOptions}
							disabled={!stepSyncReportEnabled}
							value={roleOptions.filter((role) =>
								stepSyncSuccessRoleIds.includes(String(role?.role_id))
							)}
							onChange={(_, selected) =>
								setStepSyncSuccessRoleIds(selected.map((role) => String(role.role_id)))
							}
							isOptionEqualToValue={(option, value) =>
								Number(option?.role_id) === Number(value?.role_id)
							}
							getOptionLabel={(role) =>
								`${role?.role_name || ""} (${role?.users_count ?? 0} user(s))`
							}
							renderInput={(params) => (
								<TextField
									{...params}
									label={t("UI_TEXT.STEP_SYNC_SUCCESS_ROLES", {
										defaultValue: "Report Recipients — Roles",
									})}
									placeholder={t("UI_TEXT.STEP_SYNC_SUCCESS_ROLES_PLACEHOLDER", {
										defaultValue: "Select one or more roles",
									})}
									helperText={t("UI_TEXT.STEP_SYNC_SUCCESS_ROLES_HELP", {
										defaultValue:
											"Every active user assigned to a selected role receives the report. The list is resolved each time a report is sent, so people who join or leave these roles are picked up automatically — there is nothing to keep up to date by hand.",
									})}
								/>
							)}
							sx={{ mb: 2 }}
						/>

						<Box
							sx={{
								mt: 2,
								display: "flex",
								gap: 1,
								flexWrap: "wrap",
								justifyContent: "flex-end",
							}}
						>
							{/* Preview is safe to press at any time -- it emails nobody and
							    marks no rows -- so it is the one to reach for when the
							    nightly report did not arrive and you need to know why. */}
							<Button
								variant="outlined"
								onClick={() => handleRunStepSyncReport("preview")}
								disabled={!!stepSyncReportTesting || stepSyncReportSaving}
							>
								{stepSyncReportTesting === "preview"
									? t("BUTTON.CHECKING", { defaultValue: "Checking..." })
									: t("BUTTON.PREVIEW_REPORT", { defaultValue: "Preview Report" })}
							</Button>
							<Button
								variant="outlined"
								color="warning"
								onClick={() => handleRunStepSyncReport("send")}
								disabled={!stepSyncReportEnabled || !!stepSyncReportTesting || stepSyncReportSaving}
							>
								{stepSyncReportTesting === "send"
									? t("BUTTON.SENDING", { defaultValue: "Sending..." })
									: t("BUTTON.SEND_REPORT_NOW", { defaultValue: "Send Report Now" })}
							</Button>
							<Button
								variant="contained"
								onClick={handleSaveStepSyncReport}
								startIcon={<Save />}
								disabled={stepSyncReportSaving || !!stepSyncReportTesting}
							>
								{stepSyncReportSaving
									? t("BUTTON.SAVING", { defaultValue: "Saving..." })
									: t("BUTTON.SAVE_CHANGES", { defaultValue: "Save Settings" })}
							</Button>
						</Box>
					</Box>
				)}
				{isSuperAdmin && tab === missingSiteConfigTabIndex && (
					<Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, p: 2, mb: 3 }}>
						<Typography variant="body1" sx={{ mb: 2 }} fontWeight={"bold"}>
							{t("LABELS.MISSING_SITE_CONFIG_ROLE", { defaultValue: "Missing Site Config Role" })}
						</Typography>
						<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
							{t("UI_TEXT.MISSING_SITE_CONFIG_HELP", {
								defaultValue:
									"Who is emailed when a user is approved for a bakery location that cannot be priced (no Legal Entity, no Currency, or no Dynamics 365 exchange rate).",
							})}
						</Typography>

						<FormControl fullWidth size="small">
							<InputLabel>
								{t("LABELS.MISSING_SITE_CONFIG_ROLES", { defaultValue: "Notify Roles" })}
							</InputLabel>
							<Select
								multiple
								value={missingSiteConfigRoles}
								label={t("LABELS.MISSING_SITE_CONFIG_ROLES", { defaultValue: "Notify Roles" })}
								onChange={(e) => {
									const nextValue = Array.isArray(e.target.value) ? e.target.value : [e.target.value];
									setMissingSiteConfigRoles(
										Array.from(new Set(nextValue.map((value) => String(value || "").trim()).filter(Boolean)))
									);
								}}
								renderValue={(selected) => {
									if (!Array.isArray(selected) || selected.length === 0) {
										return t("UI_TEXT.NO_ROLES_SELECTED", { defaultValue: "No roles selected" });
									}
									// Stored value is the role id; show the name so a renamed role
									// still reads correctly without rewriting the setting.
									return selected
										.map((roleId) => {
											const match = roleOptions.find((role) => String(role?.role_id) === String(roleId));
											return match?.role_name || roleId;
										})
										.join(", ");
								}}
							>
								{roleOptions.map((role) => (
									<MenuItem key={role?.role_id} value={String(role?.role_id)}>
										<Checkbox
											checked={missingSiteConfigRoles.includes(String(role?.role_id))}
											size="small"
										/>
										{role?.role_name}
									</MenuItem>
								))}
							</Select>
						</FormControl>

						<FormControlLabel
							sx={{ mt: 2 }}
							control={
								<Switch
									checked={missingSiteConfigUseCcr}
									onChange={(e) => setMissingSiteConfigUseCcr(e.target.checked)}
								/>
							}
							label={t("LABELS.MISSING_SITE_CONFIG_USE_CCR", {
								defaultValue: "Also send to the bakery location's CCR email",
							})}
						/>
						<Typography variant="caption" color="text.secondary" display="block">
							{t("UI_TEXT.MISSING_SITE_CONFIG_CCR_HELP", {
								defaultValue:
									"Takes the CCR email from the bakery location record, so each alert reaches that location's own CCR. Locations with no CCR email fall back to the roles above.",
							})}
						</Typography>

						<Box textAlign="right" sx={{ mt: 2 }}>
							<Button
								variant="contained"
								onClick={handleSaveMissingSiteConfigSettings}
								startIcon={<Save />}
								disabled={missingSiteConfigSaving}
							>
								{missingSiteConfigSaving
									? t("BUTTON.SAVING", { defaultValue: "Saving..." })
									: t("BUTTON.SAVE_CHANGES", { defaultValue: "Save Settings" })}
							</Button>
						</Box>
					</Box>
				)}
			</Box>

		</>
	);
};

export default Settings;
